import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PDFCheckBox, PDFDocument, PDFDropdown, PDFField, PDFOptionList, PDFRadioGroup, PDFTextField, StandardFonts, rgb } from 'pdf-lib';
import apiClient from '@/api/axios';
import edgelexLogoStatic from '@/assets/edgelex-logo-static.png';
import AttachmentPickerModal from '@/features/edgemail-client/components/compose/AttachmentPickerModal';
import { LexConversationStream } from '@/features/lexRuntime/components/LexConversationStream';
import { LexInputBar } from '@/features/lexRuntime/components/LexInputBar';
import { useLexRuntimeStore } from '@/features/lexRuntime/store/lexRuntimeStore';
import { PdfViewer, type PdfViewerOverlay } from '@/shared/components/PdfViewer';
import { FontDownloadService } from '@/features/wordprocessing/rendering/fonts/FontDownloadService';
import { requestPrintBillingApproval } from '@/features/print-billing/printBillingApproval';

type PdfFieldType = 'text' | 'checkbox' | 'radio' | 'dropdown' | 'option_list' | 'unsupported';

type PdfFieldRect = { x: number; y: number; width: number; height: number };

type PdfFieldModel = {
  name: string;
  label: string;
  type: PdfFieldType;
  options?: string[];
  page?: number;
  required?: boolean;
  /** Bounding box in PDF-viewer coordinates (origin top-left, in points). */
  rect?: PdfFieldRect;
};

type PdfSessionModel = {
  session_id: string;
  status: 'active' | 'committed' | 'abandoned';
  autosave_count: number;
  last_saved_at: string | null;
  field_values: Record<string, string | boolean | string[] | null> | null;
};

type FilingIssueModel = {
  code: string;
  severity: 'blocker' | 'warning';
  message: string;
  field_name?: string;
  field_label?: string;
};

type FilingReadinessModel = {
  status: 'ready' | 'warnings' | 'blockers';
  blockers: FilingIssueModel[];
  warnings: FilingIssueModel[];
  checked_at: string;
  required_fields_considered: number;
  required_fields_missing: number;
};

type PdfFieldChangeModel = {
  field_name: string;
  field_label: string;
  before: string | boolean | string[] | null;
  after: string | boolean | string[] | null;
  change_type: 'added' | 'removed' | 'modified';
};

type DeadlineSuggestionModel = {
  suggestion_id: string;
  source_field_name: string;
  source_field_label: string;
  due_at: string;
  task_title: string;
  event_title: string;
  confidence: 'high' | 'medium';
  reason: string;
};

type PdfValidationModel = {
  readiness: FilingReadinessModel;
  changed_fields: PdfFieldChangeModel[];
  deadline_suggestions: DeadlineSuggestionModel[];
  summary: {
    changed_field_count: number;
    blocker_count: number;
    warning_count: number;
  };
};

type PdfFieldValue = string | boolean | string[] | null;

type OverlayFieldType = 'text' | 'checkbox' | 'date' | 'signature' | 'initial' | 'freeform_text';
type TextAnnotationFontFamily = 'Helvetica' | 'Times-Roman' | 'Courier';

type TextAnnotationStyle = {
  font_family: TextAnnotationFontFamily;
  color: string;
  bold: boolean;
  italic: boolean;
};

type SignaturePresetKind = 'signature' | 'initial';
type SignaturePresetMode = 'draw' | 'type' | 'upload';
type SignatureTypedStyle =
  | 'great_vibes'
  | 'allura'
  | 'alex_brush'
  | 'sacramento'
  | 'dancing_script'
  | 'script'
  | 'formal'
  | 'classic';

type SignatureStyleOption = {
  value: SignatureTypedStyle;
  label: string;
  fontUid: string;
  cssFamily: string;
  fallbackFamily: string;
};

type SignaturePresetModel = {
  id: string;
  kind: SignaturePresetKind;
  mode: SignaturePresetMode;
  label: string;
  data_url: string | null;
  typed_value: string | null;
  typed_style: SignatureTypedStyle | null;
  created_at: string;
};

type OverlayFieldModel = {
  id: string;
  name: string;
  label: string;
  type: OverlayFieldType;
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
  font_size: number;
  required: boolean;
  signature_preset_id?: string | null;
  signature_style?: SignatureTypedStyle | null;
  annotation_style?: TextAnnotationStyle | null;
};

type Props = {
  docId: string;
  versionId?: string;
  titleFromUrl?: string;
  onOpenFailure?: (failure: { status: number | null; message: string }) => void;
};

type SignatureEvidenceSummaryModel = {
  sessionId: string | null;
  committedAt: string | null;
  policyLabel: string;
  evidenceHash: string | null;
  eventChainValid: boolean | null;
  exportEndpoint: string | null;
  generatedAt: string | null;
  signerNames: string[];
  latestSignedAt: string | null;
  canDownload: boolean;
  accessDenied: boolean;
};

const OVERLAY_SCHEMA_KEY = '__edgelex_overlay_schema_v1__';
const SIGNATURE_PRESET_STORAGE_KEY = 'edgelex_pdf_signature_presets_v1';
const signatureStyleOptions: SignatureStyleOption[] = [
  {
    value: 'great_vibes',
    label: 'Great Vibes',
    fontUid: 'GreatVibes-Regular.ttf',
    cssFamily: 'EdgeLexSigGreatVibes',
    fallbackFamily: '"Brush Script MT","Segoe Script","Lucida Handwriting",cursive',
  },
  {
    value: 'allura',
    label: 'Allura',
    fontUid: 'Allura-Regular.ttf',
    cssFamily: 'EdgeLexSigAllura',
    fallbackFamily: '"Apple Chancery","URW Chancery L","Segoe Script",cursive',
  },
  {
    value: 'alex_brush',
    label: 'Alex Brush',
    fontUid: 'AlexBrush-Regular.ttf',
    cssFamily: 'EdgeLexSigAlexBrush',
    fallbackFamily: '"Bradley Hand","Segoe Print","Comic Sans MS",cursive',
  },
  {
    value: 'sacramento',
    label: 'Sacramento',
    fontUid: 'Sacramento-Regular.ttf',
    cssFamily: 'EdgeLexSigSacramento',
    fallbackFamily: '"Snell Roundhand","Segoe Script","Brush Script MT",cursive',
  },
  {
    value: 'dancing_script',
    label: 'Dancing Script',
    fontUid: 'DancingScript-Regular.ttf',
    cssFamily: 'EdgeLexSigDancingScript',
    fallbackFamily: '"Segoe Print","Comic Sans MS","Brush Script MT",cursive',
  },
];

const signatureStyleOptionByValue: Record<string, SignatureStyleOption> = Object.fromEntries(
  signatureStyleOptions.map((option) => [option.value, option]),
);

const loadedSignatureFontFamilies = new Set<string>();
const signatureFontBlobUrls = new Map<string, string>();

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
}

function normalizeString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function normalizeBoolean(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null;
}

function formatDateTime(value: string | null): string {
  if (!value) return 'Not available';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
}

function formatEvidenceHash(value: string | null): string {
  if (!value) return 'Not available';
  if (value.length <= 20) return value;
  return `${value.slice(0, 12)}...${value.slice(-8)}`;
}

function extractDownloadFileName(contentDisposition: unknown, fallback: string): string {
  if (typeof contentDisposition !== 'string') {
    return fallback;
  }
  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }
  const asciiMatch = contentDisposition.match(/filename=\"?([^\";]+)\"?/i);
  if (asciiMatch?.[1]) {
    return asciiMatch[1];
  }
  return fallback;
}

function parseSignatureEvidenceSummaryFromVersion(versionCandidate: unknown): SignatureEvidenceSummaryModel | null {
  const versionRecord = asRecord(versionCandidate);
  const checkinMetadata = asRecord(versionRecord.checkin_metadata);
  const signatureAudit = asRecord(checkinMetadata.signature_audit);
  if (Object.keys(signatureAudit).length === 0) {
    return null;
  }

  const evidence = asRecord(signatureAudit.evidence);
  const signerSummary = asRecord(signatureAudit.signer_summary);
  const signedUsers = Array.isArray(signerSummary.signed_users) ? signerSummary.signed_users : [];
  const signerNames = signedUsers
    .map((entry) => normalizeString(asRecord(entry).display_name))
    .filter((name): name is string => Boolean(name));

  const policyProfileKey = normalizeString(signatureAudit.policy_profile_key);
  const policyProfileId = normalizeString(signatureAudit.policy_profile_id);
  const exportEndpoint = normalizeString(evidence.export_endpoint);

  return {
    sessionId: normalizeString(signatureAudit.session_id),
    committedAt: normalizeString(signatureAudit.committed_at),
    policyLabel: policyProfileKey || policyProfileId || 'Not specified',
    evidenceHash: normalizeString(evidence.evidence_hash),
    eventChainValid: normalizeBoolean(evidence.event_chain_valid),
    exportEndpoint,
    generatedAt: normalizeString(evidence.generated_at),
    signerNames,
    latestSignedAt: null,
    canDownload: Boolean(exportEndpoint),
    accessDenied: false,
  };
}

function parseEvidenceSigners(evidencePayload: unknown): string[] {
  const evidence = asRecord(evidencePayload);
  const fields = Array.isArray(evidence.fields) ? evidence.fields : [];
  const names = new Set<string>();

  for (const rawField of fields) {
    const field = asRecord(rawField);
    const signedByDisplay = normalizeString(field.signed_by_display);
    const signerDisplay = normalizeString(field.signer_display);
    if (signedByDisplay) {
      names.add(signedByDisplay);
      continue;
    }
    if (signerDisplay) {
      names.add(signerDisplay);
    }
  }

  return Array.from(names);
}

function parseLatestSignedAt(evidencePayload: unknown): string | null {
  const evidence = asRecord(evidencePayload);
  const fields = Array.isArray(evidence.fields) ? evidence.fields : [];
  let latest: string | null = null;

  for (const rawField of fields) {
    const field = asRecord(rawField);
    const signedAt = normalizeString(field.signed_at);
    if (!signedAt) continue;
    if (!latest || signedAt > latest) {
      latest = signedAt;
    }
  }

  return latest;
}

function getApiErrorMessage(error: unknown, fallback: string): string {
  const err = error as any;
  return (
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    (error instanceof Error ? error.message : fallback)
  );
}

function getApiErrorStatus(error: unknown): number | null {
  const err = error as any;
  const status = err?.response?.status;
  return typeof status === 'number' ? status : null;
}

function isReadOnlyPdfFallbackError(status: number | null, message: string): boolean {
  const normalized = message.toLowerCase();
  if (normalized.includes('no editable acroform fields')) return true;
  if (normalized.includes('no fillable') || normalized.includes('no editable fields')) return true;
  if (normalized.includes('cannot be normalized for reliable editing')) return true;
  if (normalized.includes('cannot be normalized')) return true;
  if (normalized.includes('failed to parse pdf')) return true;
  if ((status === 400 || status === 422) &&
      (normalized.includes('acroform') || normalized.includes('fillable'))) {
    return true;
  }
  return false;
}

function isUuidLike(value: string | undefined | null): boolean {
  if (!value) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

function resolveRequestedVersionId(documentData: any, versionRef?: string): string | undefined {
  const normalizedRef = String(versionRef || '').trim();
  const latestVersionId = String(
    documentData?.latest_version?.version_id ||
    documentData?.latestVersion?.version_id ||
    '',
  ).trim();

  if (!normalizedRef) {
    return latestVersionId || undefined;
  }

  if (normalizedRef === latestVersionId) {
    return latestVersionId || undefined;
  }

  const versions = Array.isArray(documentData?.versions) ? documentData.versions : [];
  const matched = versions.find((version: any) =>
    version?.version_id === normalizedRef ||
    version?.id === normalizedRef ||
    version?.version_number === normalizedRef ||
    version?.versionNumber === normalizedRef,
  );

  const matchedId = matched?.version_id || matched?.id;
  if (matchedId) {
    return String(matchedId);
  }

  if (isUuidLike(normalizedRef)) {
    return normalizedRef;
  }

  return latestVersionId || undefined;
}

function formatFieldValue(value: string | boolean | string[] | null | undefined): string {
  if (value === null || value === undefined) return 'empty';
  if (typeof value === 'boolean') return value ? 'checked' : 'unchecked';
  if (Array.isArray(value)) return value.length > 0 ? value.join(', ') : 'empty';
  return value.trim() || 'empty';
}

function createOverlayId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `overlay_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function slugifyFieldLabel(label: string): string {
  const slug = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 48);
  return slug || 'field';
}

/** Build a short document slug from the document title for overlay field names. */
function buildDocSlug(title: string): string {
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 24);
  return slug || 'doc';
}

/**
 * Build a human-readable, document-anchored overlay field name.
 * Format: {doc_slug}.p{page}.{label_slug}
 * Falls back to adding a counter suffix only when needed for uniqueness.
 */
function buildOverlayFieldName(
  docSlug: string,
  page: number,
  label: string,
  existingNames: Set<string>,
): string {
  const labelSlug = slugifyFieldLabel(label);
  const base = `${docSlug}.p${page}.${labelSlug}`;
  if (!existingNames.has(base)) return base;
  // Add counter suffix for uniqueness
  let counter = 2;
  while (existingNames.has(`${base}_${counter}`)) {
    counter++;
  }
  return `${base}_${counter}`;
}

function createDefaultOverlayField(
  index: number,
  page: number,
  docSlug?: string,
  existingNames?: Set<string>,
): OverlayFieldModel {
  return createDefaultOverlayFieldByType(index, page, 'text', { docSlug, existingNames });
}

function createDefaultOverlayFieldByType(
  index: number,
  page: number,
  type: OverlayFieldType,
  options?: { label?: string; docSlug?: string; existingNames?: Set<string> },
): OverlayFieldModel {
  const id = createOverlayId();
  const defaultLabel = type === 'signature'
    ? 'Signature'
    : type === 'initial'
      ? 'Initial'
      : type === 'freeform_text'
        ? 'Text'
        : `Field ${index}`;
  const label = (options?.label || defaultLabel).trim();
  const docSlug = options?.docSlug || 'doc';
  const existingNames = options?.existingNames || new Set<string>();
  const name = buildOverlayFieldName(docSlug, Math.max(1, page), label, existingNames);
  const dimensions = type === 'signature'
    ? { width: 220, height: 56, font_size: 24 }
    : type === 'initial'
      ? { width: 130, height: 44, font_size: 20 }
      : type === 'freeform_text'
        ? { width: 200, height: 30, font_size: 12 }
        : { width: 220, height: 20, font_size: 11 };
  return {
    id,
    name,
    label,
    type,
    page: Math.max(1, page),
    x: 64,
    y: 64 + ((index - 1) % 12) * (type === 'signature' || type === 'initial' ? 52 : 28),
    width: dimensions.width,
    height: dimensions.height,
    font_size: dimensions.font_size,
    required: false,
    signature_preset_id: null,
    signature_style: null,
    annotation_style: type === 'freeform_text'
      ? { font_family: 'Helvetica', color: '#000000', bold: false, italic: false }
      : null,
  };
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const normalized = String(hex || '#000000').replace('#', '').padEnd(6, '0').slice(0, 6);
  const r = Math.max(0, Math.min(1, (parseInt(normalized.slice(0, 2), 16) || 0) / 255));
  const g = Math.max(0, Math.min(1, (parseInt(normalized.slice(2, 4), 16) || 0) / 255));
  const b = Math.max(0, Math.min(1, (parseInt(normalized.slice(4, 6), 16) || 0) / 255));
  return { r, g, b };
}

function isDataUrlImage(value: unknown): value is string {
  return typeof value === 'string' && /^data:image\/[a-zA-Z0-9.+-]+;base64,/.test(value);
}

function isSignatureTypedStyle(value: unknown): value is SignatureTypedStyle {
  return value === 'great_vibes'
    || value === 'allura'
    || value === 'alex_brush'
    || value === 'sacramento'
    || value === 'dancing_script'
    || value === 'script'
    || value === 'formal'
    || value === 'classic';
}

function normalizeSignatureStyle(style: SignatureTypedStyle | null | undefined): SignatureTypedStyle {
  if (!style) return 'great_vibes';
  if (style === 'script') return 'great_vibes';
  if (style === 'formal') return 'allura';
  if (style === 'classic') return 'alex_brush';
  return style;
}

function signatureStyleConfig(style: SignatureTypedStyle | null | undefined): SignatureStyleOption {
  const normalized = normalizeSignatureStyle(style);
  return signatureStyleOptionByValue[normalized] || signatureStyleOptions[0];
}

function signatureFontFamily(style: SignatureTypedStyle | null | undefined): string {
  const option = signatureStyleConfig(style);
  if (loadedSignatureFontFamilies.has(option.cssFamily)) {
    return `${option.cssFamily}, ${option.fallbackFamily}`;
  }
  return option.fallbackFamily;
}

async function ensureSignatureWebFont(style: SignatureTypedStyle | null | undefined): Promise<void> {
  const option = signatureStyleConfig(style);
  if (loadedSignatureFontFamilies.has(option.cssFamily)) {
    return;
  }

  try {
    const bytes = await FontDownloadService.downloadFont(option.fontUid, option.fontUid);
    const mime = option.fontUid.toLowerCase().endsWith('.otf') ? 'font/otf' : 'font/ttf';
    const blob = new Blob([bytes], { type: mime });
    const objectUrl = URL.createObjectURL(blob);
    signatureFontBlobUrls.set(option.cssFamily, objectUrl);
    const fontFace = new FontFace(option.cssFamily, `url(${objectUrl})`);
    await fontFace.load();
    (document as any).fonts?.add(fontFace);
    loadedSignatureFontFamilies.add(option.cssFamily);
  } catch {
    // Fallback to system cursive if custom signature font is unavailable.
  }
}

async function buildTypedSignatureDataUrl(
  value: string,
  style: SignatureTypedStyle | null | undefined,
  kind: SignaturePresetKind,
): Promise<string | null> {
  const normalized = value.trim();
  if (!normalized) return null;

  await ensureSignatureWebFont(style);
  const cssFont = signatureFontFamily(style);
  const canvas = document.createElement('canvas');
  canvas.width = kind === 'initial' ? 700 : 1200;
  canvas.height = kind === 'initial' ? 220 : 320;
  const context = canvas.getContext('2d');
  if (!context) return null;

  const fontSize = kind === 'initial' ? 120 : 140;
  context.clearRect(0, 0, canvas.width, canvas.height);
  context.fillStyle = '#000000';
  context.textBaseline = 'alphabetic';
  context.font = `${fontSize}px ${cssFont}`;

  if ((document as any).fonts?.ready) {
    try {
      await (document as any).fonts.ready;
    } catch {
      // Ignore font readiness race; fallback font will still draw.
    }
  }

  const textWidth = context.measureText(normalized).width;
  const x = Math.max(24, (canvas.width - textWidth) / 2);
  const y = kind === 'initial' ? 168 : 236;
  context.fillText(normalized, x, y);
  return canvas.toDataURL('image/png');
}

function deserializeSignaturePresets(raw: string | null): SignaturePresetModel[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((entry) => {
        const item = asRecord(entry);
        const kind = item.kind === 'initial' ? 'initial' : item.kind === 'signature' ? 'signature' : null;
        const mode = item.mode === 'draw' || item.mode === 'type' || item.mode === 'upload' ? item.mode : null;
        if (!kind || !mode) return null;
        const label = normalizeString(item.label) || (kind === 'signature' ? 'Signature' : 'Initial');
        const preset: SignaturePresetModel = {
          id: normalizeString(item.id) || createOverlayId(),
          kind,
          mode,
          label,
          data_url: normalizeString(item.data_url),
          typed_value: normalizeString(item.typed_value),
          typed_style: isSignatureTypedStyle(item.typed_style) ? normalizeSignatureStyle(item.typed_style) : null,
          created_at: normalizeString(item.created_at) || new Date().toISOString(),
        };
        return preset;
      })
      .filter((entry): entry is SignaturePresetModel => Boolean(entry));
  } catch {
    return [];
  }
}

function serializeSignaturePresets(presets: SignaturePresetModel[]): string {
  return JSON.stringify(
    presets.map((preset) => ({
      id: preset.id,
      kind: preset.kind,
      mode: preset.mode,
      label: preset.label,
      data_url: preset.data_url,
      typed_value: preset.typed_value,
      typed_style: preset.typed_style,
      created_at: preset.created_at,
    })),
  );
}

async function renderOverlayPdf(
  baseBytes: Uint8Array,
  fieldsToRender: OverlayFieldModel[],
  valuesToRender: Record<string, PdfFieldValue>,
): Promise<Uint8Array> {
  const overlayDoc = await PDFDocument.load(baseBytes, { ignoreEncryption: true });
  const overlayFont = await overlayDoc.embedFont(StandardFonts.Helvetica);
  const signatureScriptFont = await overlayDoc.embedFont(StandardFonts.TimesRomanItalic);
  const signatureFormalFont = await overlayDoc.embedFont(StandardFonts.TimesRoman);
  const signatureClassicFont = await overlayDoc.embedFont(StandardFonts.CourierOblique);
  // Freeform text annotation fonts — only embed if at least one freeform_text field
  // has content to render, so we don't pollute the PDF resource dictionary needlessly.
  const hasFreeformContent = fieldsToRender.some(
    (f) => f.type === 'freeform_text' &&
      typeof valuesToRender[f.name] === 'string' &&
      (valuesToRender[f.name] as string).trim().length > 0,
  );
  let freeformHelveticaBold: Awaited<ReturnType<typeof overlayDoc.embedFont>> | undefined;
  let freeformHelveticaOblique: Awaited<ReturnType<typeof overlayDoc.embedFont>> | undefined;
  let freeformHelveticaBoldOblique: Awaited<ReturnType<typeof overlayDoc.embedFont>> | undefined;
  let freeformTimesRomanBold: Awaited<ReturnType<typeof overlayDoc.embedFont>> | undefined;
  let freeformTimesRomanBoldItalic: Awaited<ReturnType<typeof overlayDoc.embedFont>> | undefined;
  let freeformCourier: Awaited<ReturnType<typeof overlayDoc.embedFont>> | undefined;
  let freeformCourierBold: Awaited<ReturnType<typeof overlayDoc.embedFont>> | undefined;
  let freeformCourierBoldOblique: Awaited<ReturnType<typeof overlayDoc.embedFont>> | undefined;
  if (hasFreeformContent) {
    freeformHelveticaBold = await overlayDoc.embedFont(StandardFonts.HelveticaBold);
    freeformHelveticaOblique = await overlayDoc.embedFont(StandardFonts.HelveticaOblique);
    freeformHelveticaBoldOblique = await overlayDoc.embedFont(StandardFonts.HelveticaBoldOblique);
    freeformTimesRomanBold = await overlayDoc.embedFont(StandardFonts.TimesRomanBold);
    freeformTimesRomanBoldItalic = await overlayDoc.embedFont(StandardFonts.TimesRomanBoldItalic);
    freeformCourier = await overlayDoc.embedFont(StandardFonts.Courier);
    freeformCourierBold = await overlayDoc.embedFont(StandardFonts.CourierBold);
    freeformCourierBoldOblique = await overlayDoc.embedFont(StandardFonts.CourierBoldOblique);
  }

  for (const field of fieldsToRender) {
    const pageIndex = Math.max(0, field.page - 1);
    if (pageIndex >= overlayDoc.getPageCount()) {
      continue;
    }
    const page = overlayDoc.getPage(pageIndex);
    const pageWidth = page.getWidth();
    const pageHeight = page.getHeight();
    const x = Math.max(0, Math.min(field.x, pageWidth - 8));
    const yTop = Math.max(0, Math.min(field.y, pageHeight - 8));

    if (field.type === 'checkbox') {
      const checked = Boolean(valuesToRender[field.name]);
      const boxSize = Math.max(10, Math.min(field.width, field.height, 16));
      const boxY = Math.max(0, pageHeight - yTop - boxSize);
      page.drawRectangle({
        x,
        y: boxY,
        width: boxSize,
        height: boxSize,
        borderWidth: 1,
        borderColor: rgb(0, 0, 0),
        color: rgb(1, 1, 1),
      });
      if (checked) {
        page.drawText('X', {
          x: x + 2,
          y: boxY + 1,
          size: Math.max(8, boxSize - 4),
          font: overlayFont,
          color: rgb(0, 0, 0),
        });
      }
      continue;
    }

    const rawValue = valuesToRender[field.name];

    if (field.type === 'freeform_text') {
      const textContent = typeof rawValue === 'string' ? rawValue.trim() : '';
      if (!textContent) {
        continue;
      }
      const style = field.annotation_style;
      const fontFamily: TextAnnotationFontFamily = style?.font_family || 'Helvetica';
      const bold = style?.bold ?? false;
      const italic = style?.italic ?? false;
      const colorRgb = hexToRgb(style?.color || '#000000');
      let selectedFont;
      if (fontFamily === 'Times-Roman') {
        selectedFont = bold && italic ? (freeformTimesRomanBoldItalic ?? signatureFormalFont)
          : bold ? (freeformTimesRomanBold ?? signatureFormalFont)
          : italic ? signatureScriptFont
          : signatureFormalFont;
      } else if (fontFamily === 'Courier') {
        selectedFont = bold && italic ? (freeformCourierBoldOblique ?? signatureClassicFont)
          : bold ? (freeformCourierBold ?? signatureClassicFont)
          : italic ? signatureClassicFont
          : (freeformCourier ?? signatureClassicFont);
      } else {
        selectedFont = bold && italic ? (freeformHelveticaBoldOblique ?? overlayFont)
          : bold ? (freeformHelveticaBold ?? overlayFont)
          : italic ? (freeformHelveticaOblique ?? overlayFont)
          : overlayFont;
      }
      const drawY = Math.max(0, pageHeight - yTop - field.font_size);
      page.drawText(textContent, {
        x,
        y: drawY,
        size: field.font_size,
        font: selectedFont,
        color: rgb(colorRgb.r, colorRgb.g, colorRgb.b),
        maxWidth: Math.max(40, field.width),
        lineHeight: field.font_size + 2,
      });
      continue;
    }

    if (field.type === 'signature' || field.type === 'initial') {
      if (isDataUrlImage(rawValue)) {
        const commaIndex = rawValue.indexOf(',');
        if (commaIndex > 0) {
          const base64Payload = rawValue.slice(commaIndex + 1);
          try {
            const imageBytes = base64ToUint8(base64Payload);
            const image = rawValue.startsWith('data:image/png')
              ? await overlayDoc.embedPng(imageBytes)
              : await overlayDoc.embedJpg(imageBytes);
            const scale = Math.min(
              Math.max(0.2, field.width / Math.max(1, image.width)),
              Math.max(0.2, field.height / Math.max(1, image.height)),
            );
            const drawWidth = image.width * scale;
            const drawHeight = image.height * scale;
            const drawY = Math.max(0, pageHeight - yTop - drawHeight);
            page.drawImage(image, {
              x,
              y: drawY,
              width: drawWidth,
              height: drawHeight,
            });
            continue;
          } catch {
            // Fall through to text rendering if image parsing fails.
          }
        }
      }

      const typedSignature = typeof rawValue === 'string' ? rawValue.trim() : '';
      if (!typedSignature) {
        continue;
      }
      const normalizedStyle = normalizeSignatureStyle(field.signature_style);
      const signatureFont = normalizedStyle === 'allura'
        ? signatureFormalFont
        : normalizedStyle === 'alex_brush'
          ? signatureClassicFont
          : signatureScriptFont;
      const signatureFontSize = Math.max(14, field.font_size);
      const drawY = Math.max(0, pageHeight - yTop - signatureFontSize);
      page.drawText(typedSignature, {
        x,
        y: drawY,
        size: signatureFontSize,
        font: signatureFont,
        color: rgb(0, 0, 0),
        maxWidth: Math.max(40, field.width),
      });
      continue;
    }

    const textValue = typeof rawValue === 'string'
      ? rawValue
      : Array.isArray(rawValue)
        ? rawValue.join(', ')
        : rawValue === null || rawValue === undefined
          ? ''
          : String(rawValue);
    if (!textValue.trim()) {
      continue;
    }

    const drawY = Math.max(0, pageHeight - yTop - field.font_size);
    page.drawText(textValue, {
      x,
      y: drawY,
      size: field.font_size,
      font: overlayFont,
      color: rgb(0, 0, 0),
      maxWidth: Math.max(40, field.width),
    });
  }

  return overlayDoc.save({ updateFieldAppearances: false, useObjectStreams: false });
}

function isOverlayFieldType(value: unknown): value is OverlayFieldType {
  return value === 'text' || value === 'checkbox' || value === 'date' || value === 'signature' || value === 'initial' || value === 'freeform_text';
}

function sanitizeOverlayField(
  raw: Partial<OverlayFieldModel>,
  index: number,
  docSlug?: string,
  existingNames?: Set<string>,
): OverlayFieldModel {
  const fallback = createDefaultOverlayField(index, 1, docSlug, existingNames);
  const id = typeof raw.id === 'string' && raw.id.trim() ? raw.id : fallback.id;
  const label = typeof raw.label === 'string' && raw.label.trim() ? raw.label.trim() : fallback.label;
  // Preserve existing persisted names for backwards compatibility with saved sessions
  const explicitName = typeof raw.name === 'string' && raw.name.trim() ? raw.name.trim() : '';
  const page = Number.isFinite(raw.page) ? Math.max(1, Math.trunc(raw.page as number)) : fallback.page;
  const resolvedName = explicitName || buildOverlayFieldName(docSlug || 'doc', page, label, existingNames || new Set());
  const resolvedType = isOverlayFieldType(raw.type) ? raw.type : fallback.type;
  let parsedAnnotationStyle: TextAnnotationStyle | null = null;
  if (resolvedType === 'freeform_text' && raw.annotation_style && typeof raw.annotation_style === 'object') {
    const rawStyle = raw.annotation_style as Record<string, unknown>;
    const fontFamily = rawStyle.font_family === 'Times-Roman' || rawStyle.font_family === 'Courier'
      ? rawStyle.font_family as TextAnnotationFontFamily
      : 'Helvetica';
    const color = typeof rawStyle.color === 'string' && /^#[0-9a-fA-F]{3,8}$/.test(rawStyle.color)
      ? rawStyle.color
      : '#000000';
    parsedAnnotationStyle = {
      font_family: fontFamily,
      color,
      bold: Boolean(rawStyle.bold),
      italic: Boolean(rawStyle.italic),
    };
  }

  return {
    id,
    name: resolvedName,
    label,
    type: resolvedType,
    page,
    x: Number.isFinite(raw.x) ? Math.max(0, raw.x as number) : fallback.x,
    y: Number.isFinite(raw.y) ? Math.max(0, raw.y as number) : fallback.y,
    width: Number.isFinite(raw.width) ? Math.max(24, raw.width as number) : fallback.width,
    height: Number.isFinite(raw.height) ? Math.max(16, raw.height as number) : fallback.height,
    font_size: Number.isFinite(raw.font_size)
      ? Math.max(8, Math.min(resolvedType === 'freeform_text' ? 72 : 24, raw.font_size as number))
      : fallback.font_size,
    required: Boolean(raw.required),
    signature_preset_id: typeof raw.signature_preset_id === 'string' ? raw.signature_preset_id : null,
    signature_style: isSignatureTypedStyle(raw.signature_style) ? normalizeSignatureStyle(raw.signature_style) : null,
    annotation_style: parsedAnnotationStyle,
  };
}

function parseOverlaySchema(rawSchema: unknown): OverlayFieldModel[] {
  if (typeof rawSchema !== 'string' || !rawSchema.trim()) {
    return [];
  }
  try {
    const parsed = JSON.parse(rawSchema);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.map((entry, index) => sanitizeOverlayField(entry || {}, index + 1));
  } catch {
    return [];
  }
}

function serializeOverlaySchema(fields: OverlayFieldModel[]): string {
  return JSON.stringify(fields.map((field) => ({
    id: field.id,
    name: field.name,
    label: field.label,
    type: field.type,
    page: field.page,
    x: field.x,
    y: field.y,
    width: field.width,
    height: field.height,
    font_size: field.font_size,
    required: field.required,
    signature_preset_id: field.signature_preset_id || null,
    signature_style: field.signature_style || null,
    annotation_style: field.annotation_style || null,
  })));
}

function extractPageHintFromIssue(issue: FilingIssueModel): number | null {
  const candidates = [issue.field_label, issue.field_name].filter(Boolean) as string[];
  for (const value of candidates) {
    const explicitPage = value.match(/page\s*([0-9]+)/i);
    if (explicitPage) {
      const pageNumber = Number.parseInt(explicitPage[1], 10);
      if (Number.isFinite(pageNumber) && pageNumber > 0) {
        return pageNumber;
      }
    }

    const shorthandPage = value.match(/(^|[\s._-])p([0-9]+)([\s._-]|$)/i);
    if (shorthandPage) {
      const pageNumber = Number.parseInt(shorthandPage[2], 10);
      if (Number.isFinite(pageNumber) && pageNumber > 0) {
        return pageNumber;
      }
    }
  }
  return null;
}

function uint8ToBase64(data: Uint8Array): string {
  const chunkSize = 0x8000;
  let binary = '';
  for (let index = 0; index < data.length; index += chunkSize) {
    binary += String.fromCharCode(...data.subarray(index, index + chunkSize));
  }
  return btoa(binary);
}

function base64ToUint8(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

function hasPdfHeader(data: Uint8Array | null | undefined): data is Uint8Array {
  if (!data || data.length < 5) return false;
  return String.fromCharCode(
    data[0],
    data[1],
    data[2],
    data[3],
    data[4],
  ) === '%PDF-';
}

function readFieldValue(field: PDFField): string | boolean | string[] | null {
  if (field instanceof PDFTextField) {
    return field.getText() || '';
  }
  if (field instanceof PDFCheckBox) {
    return field.isChecked();
  }
  if (field instanceof PDFRadioGroup) {
    return field.getSelected() || '';
  }
  if (field instanceof PDFDropdown) {
    const selected = field.getSelected();
    return selected.length > 0 ? selected[0] : '';
  }
  if (field instanceof PDFOptionList) {
    return field.getSelected() || [];
  }
  return null;
}

function setFieldValue(field: PDFField, value: string | boolean | string[] | null): void {
  if (field instanceof PDFTextField) {
    field.setText(typeof value === 'string' ? value : '');
    return;
  }
  if (field instanceof PDFCheckBox) {
    if (value === true) field.check();
    else field.uncheck();
    return;
  }
  if (field instanceof PDFRadioGroup) {
    if (typeof value === 'string' && value.trim()) {
      field.select(value);
    }
    return;
  }
  if (field instanceof PDFDropdown) {
    if (typeof value === 'string' && value.trim()) {
      field.select(value);
    } else {
      field.clear();
    }
    return;
  }
  if (field instanceof PDFOptionList) {
    if (Array.isArray(value) && value.length > 0) {
      field.select(value);
    } else {
      field.clear();
    }
  }
}

function inferFieldType(field: PDFField): PdfFieldType {
  if (field instanceof PDFTextField) return 'text';
  if (field instanceof PDFCheckBox) return 'checkbox';
  if (field instanceof PDFRadioGroup) return 'radio';
  if (field instanceof PDFDropdown) return 'dropdown';
  if (field instanceof PDFOptionList) return 'option_list';
  return 'unsupported';
}

// --- Field name humanization (unified with backend logic) ---

const FIELD_SEGMENT_ALIASES: Record<string, string> = {
  AttyPartyInfo: 'Attorney',
  AttyInfo: 'Attorney',
  CourtInfo: 'Court',
  CrtInfo: 'Court',
  PtfInfo: 'Plaintiff',
  DftInfo: 'Defendant',
  RespInfo: 'Respondent',
  PetInfo: 'Petitioner',
  ClmntInfo: 'Claimant',
  JdgInfo: 'Judge',
  GuardInfo: 'Guardian',
  MinorInfo: 'Minor',
  SpouseInfo: 'Spouse',
};

const TOKEN_ALIASES: Record<string, string> = {
  Atty: 'Attorney',
  Crt: 'Court',
  Ptf: 'Plaintiff',
  Dft: 'Defendant',
  Resp: 'Respondent',
  Pet: 'Petitioner',
  Clmnt: 'Claimant',
  Jdg: 'Judge',
  Dept: 'Department',
  Div: 'Division',
  Zip: 'ZIP',
  Id: 'ID',
  Ssn: 'SSN',
  Dob: 'Date of Birth',
  No: 'Number',
  Num: 'Number',
  Addr: 'Address',
  Tel: 'Telephone',
  Ph: 'Phone',
  Amt: 'Amount',
  Desc: 'Description',
  Dt: 'Date',
  Dte: 'Date',
  Sig: 'Signature',
  Init: 'Initial',
  Chk: 'Checkbox',
  Lbl: 'Label',
  Hdr: 'Header',
  Ftr: 'Footer',
  Info: 'Information',
  Lic: 'License',
  Cert: 'Certificate',
  Decl: 'Declaration',
  Stip: 'Stipulation',
  Ord: 'Order',
  Mot: 'Motion',
  Aff: 'Affidavit',
};

/** Tokens that are structural noise in court form field names and carry no user meaning. */
const NOISE_TOKENS = new Set([
  'sf', 'ft', 'tf', 'cb', 'rb', 'lb', 'hdr', 'ftr',
  'header', 'footer', 'caption', 'field', 'acroform',
  'info', 'body', 'topmostsubform', 'subform', 'form',
  'wrapper', 'container', 'group', 'item', 'row', 'cell',
]);

/** Leaf tokens that are too generic to stand alone — need parent context. */
const GENERIC_LEAFS = new Set([
  'Name', 'Street', 'City', 'State', 'ZIP', 'Phone', 'Fax',
  'Email', 'County', 'Number', 'Address', 'Date', 'Date of Birth',
  'Telephone', 'Amount', 'Description', 'Bar Number', 'Case Number',
  'Signature', 'Initial', 'License', 'SSN', 'ID',
]);

function stripSegmentIndexes(value: string): string {
  return value.replace(/\[\d+]/g, '').trim();
}

function normalizeTokenCase(token: string): string {
  const lower = token.toLowerCase();
  if (lower === 'zip') return 'ZIP';
  if (lower === 'id') return 'ID';
  if (lower === 'ssn') return 'SSN';
  return token.charAt(0).toUpperCase() + token.slice(1).toLowerCase();
}

/** True if token is structural noise that should be stripped. */
function isNoiseToken(token: string): boolean {
  const lower = token.toLowerCase();
  if (NOISE_TOKENS.has(lower)) return true;
  // Pure numbers (array indexes, counters)
  if (/^\d+$/.test(token)) return true;
  // Page indicators: P1, P12, Page1, Page12
  if (/^P\d+$/i.test(token) || /^Page\d+$/i.test(token)) return true;
  // Form code prefixes: FL300, SC100, MC350, GC-123
  if (/^[A-Z]{2,5}[-]?\d{2,}(?:[-/][A-Z0-9]+)?$/i.test(token)) return true;
  // Single uppercase abbreviations that are just structural (2-3 chars with no alias match)
  if (/^[A-Z]{2,3}$/.test(token) && !TOKEN_ALIASES[token]) return true;
  return false;
}

/** Tokenize a raw field name segment: split camelCase, underscores, hyphens. */
function tokenizeSegment(raw: string): string[] {
  const stripped = stripSegmentIndexes(raw);
  if (!stripped) return [];
  // Remove known structural prefixes like P1Header, P2Footer
  const cleaned = stripped.replace(/^P\d+(?:Header|Footer|Body)\s*/i, '').trim();
  const withSpacing = cleaned
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/[_\-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return withSpacing.split(' ').filter(Boolean);
}

/** Collapse consecutive duplicate tokens (case-insensitive). */
function collapseRepeatedTokens(tokens: string[]): string[] {
  if (tokens.length < 2) return tokens;
  // Check if first half equals second half (full duplication)
  if (tokens.length >= 4 && tokens.length % 2 === 0) {
    const half = tokens.length / 2;
    const firstHalf = tokens.slice(0, half).map((t) => t.toLowerCase()).join(' ');
    const secondHalf = tokens.slice(half).map((t) => t.toLowerCase()).join(' ');
    if (firstHalf === secondHalf) return tokens.slice(0, half);
  }
  // Remove consecutive duplicates
  const result: string[] = [tokens[0]];
  for (let i = 1; i < tokens.length; i++) {
    if (tokens[i].toLowerCase() !== tokens[i - 1].toLowerCase()) {
      result.push(tokens[i]);
    }
  }
  return result;
}

/** Extract page number from raw field name if embedded. */
function extractPageFromFieldName(fieldName: string): number | null {
  const match = fieldName.match(/\bPage\s*(\d+)\b/i) || fieldName.match(/\.P(\d+)\b/i);
  if (match) {
    const page = Number.parseInt(match[1], 10);
    if (Number.isFinite(page) && page > 0) return page;
  }
  return null;
}

/** Humanize a single segment with alias expansion and noise removal. */
function humanizeSegment(rawSegment: string): string {
  const segment = stripSegmentIndexes(rawSegment);
  if (!segment) return '';
  if (FIELD_SEGMENT_ALIASES[segment]) return FIELD_SEGMENT_ALIASES[segment];

  const tokens = tokenizeSegment(segment);
  const meaningful = tokens
    .filter((t) => !isNoiseToken(t))
    .map((t) => TOKEN_ALIASES[t] || normalizeTokenCase(t));

  return collapseRepeatedTokens(meaningful).join(' ').trim();
}

/**
 * Convert a raw AcroForm field name into a human-readable label.
 * Optionally appends "(Page N)" when pageHint is provided and the label
 * would otherwise be ambiguous.
 */
function normalizeFieldLabel(fieldName: string, pageHint?: number | null): string {
  const segments = fieldName
    .split('.')
    .map((segment) => stripSegmentIndexes(segment))
    .filter(Boolean);

  // Humanize all segments, filter empty results
  const humanized = segments
    .map((seg) => humanizeSegment(seg))
    .filter(Boolean);

  if (humanized.length === 0) return fieldName;

  // Take the last two meaningful segments (parent + leaf)
  const leaf = humanized[humanized.length - 1];
  const parent = humanized.length > 1 ? humanized[humanized.length - 2] : '';

  let label: string;
  if (!leaf) {
    label = fieldName;
  } else if (parent && GENERIC_LEAFS.has(leaf)) {
    label = `${parent} ${leaf}`.trim();
  } else {
    label = leaf;
  }

  // Append page hint for disambiguation
  if (pageHint && pageHint > 0) {
    label = `${label} (Page ${pageHint})`;
  }

  return label;
}

/**
 * Build a page ref → page number lookup from a PDFDocument.
 * Used to resolve which page an AcroForm field widget lives on.
 */
function buildPageRefMap(pdfDoc: PDFDocument): Map<string, number> {
  const map = new Map<string, number>();
  const pages = pdfDoc.getPages();
  for (let i = 0; i < pages.length; i++) {
    map.set(pages[i].ref.toString(), i + 1);
  }
  return map;
}

/**
 * Resolve the 1-based page number for a PDFField by inspecting its widget annotations.
 * Returns null if the page cannot be determined.
 */
function resolveFieldPage(field: PDFField, pageRefMap: Map<string, number>): number | null {
  try {
    const widgets = field.acroField.getWidgets();
    if (widgets.length === 0) return null;
    const pageRef = widgets[0].P();
    if (!pageRef) return null;
    return pageRefMap.get(pageRef.toString()) ?? null;
  } catch {
    return null;
  }
}

/**
 * Extract the bounding box of a field's first widget in PDF-viewer coordinates
 * (origin top-left, in points). Returns null if unavailable.
 */
function resolveFieldRect(
  field: PDFField,
  pdfDoc: PDFDocument,
  pageRefMap: Map<string, number>,
): PdfFieldRect | null {
  try {
    const widgets = field.acroField.getWidgets();
    if (widgets.length === 0) return null;
    const widget = widgets[0];
    const rect = widget.getRectangle();
    if (!rect || rect.width <= 0 || rect.height <= 0) return null;
    // Determine which page this widget is on to get page height for Y-flip
    const pageRef = widget.P();
    const pageNum = pageRef ? (pageRefMap.get(pageRef.toString()) ?? 1) : 1;
    const pageIndex = Math.max(0, pageNum - 1);
    const pageHeight = pageIndex < pdfDoc.getPageCount()
      ? pdfDoc.getPage(pageIndex).getHeight()
      : 792; // Letter height fallback
    // PDF coordinates have origin at bottom-left; viewer uses top-left
    const viewerY = pageHeight - rect.y - rect.height;
    return {
      x: rect.x,
      y: Math.max(0, viewerY),
      width: rect.width,
      height: rect.height,
    };
  } catch {
    return null;
  }
}

/**
 * Post-process field models to add page suffixes only where labels would be ambiguous.
 * Fields with unique labels keep clean names; duplicates get "(Page N)".
 */
function disambiguateFieldLabels(models: PdfFieldModel[]): void {
  // Count how many fields share each base label (without page suffix)
  const labelCounts = new Map<string, number>();
  for (const model of models) {
    const base = model.label;
    labelCounts.set(base, (labelCounts.get(base) || 0) + 1);
  }
  // Only append page for labels that appear more than once
  for (const model of models) {
    if ((labelCounts.get(model.label) || 0) > 1 && model.page && model.page > 0) {
      model.label = `${model.label} (Page ${model.page})`;
    }
  }
}

// --- Enhancement C: Matter/Client context auto-fill ---

type MatterContext = {
  matter_id: string;
  matter_number?: string;
  practice_area?: string;
  case_subtype?: string;
  jurisdiction_code?: string;
  plaintiff_petitioner?: string;
  defendant_respondent?: string;
  description?: string;
  important_dates?: Record<string, string | null | undefined>;
  estimated_value?: number;
  client?: {
    client_id: string;
    client_name?: string;
    contact_info?: {
      email?: string;
      phone?: string;
      address?: string;
    };
  };
};

type AutoFillResult = {
  filled: number;
  skipped: number;
  details: Array<{ label: string; value: string }>;
};

/**
 * Build a mapping from normalized field labels to matter/client data values.
 *
 * IMPORTANT: Court forms use different party terminology by case type:
 * - Family law (FL-) → Petitioner / Respondent
 * - Civil litigation → Plaintiff / Defendant
 * The matter entity stores `plaintiff_petitioner` and `defendant_respondent` as
 * combined fields. The match logic below maps BOTH terminologies to the correct
 * source field so the right value appears regardless of form vocabulary.
 */
function buildAutoFillMap(matter: MatterContext): Map<string, string> {
  const map = new Map<string, string>();

  const put = (label: string, value: string | undefined | null) => {
    if (value && value.trim()) map.set(label.toLowerCase(), value.trim());
  };

  // Case identification
  put('Case Number', matter.matter_number);
  put('Case No', matter.matter_number);

  // Party names — both Petitioner/Respondent AND Plaintiff/Defendant
  // map to the same underlying matter fields
  put('Plaintiff', matter.plaintiff_petitioner);
  put('Plaintiff Name', matter.plaintiff_petitioner);
  put('Petitioner', matter.plaintiff_petitioner);
  put('Petitioner Name', matter.plaintiff_petitioner);
  put('Defendant', matter.defendant_respondent);
  put('Defendant Name', matter.defendant_respondent);
  put('Respondent', matter.defendant_respondent);
  put('Respondent Name', matter.defendant_respondent);

  // Client info (client is typically the petitioner/plaintiff in the matter)
  const client = matter.client;
  if (client) {
    put('Client Name', client.client_name);
    put('Client Email', client.contact_info?.email);
    put('Client Phone', client.contact_info?.phone);
    put('Client Address', client.contact_info?.address);
    // Many court forms label the filing party generically
    put('Your Name', client.client_name);
    put('Name', client.client_name);
    put('Email', client.contact_info?.email);
    put('Phone', client.contact_info?.phone);
    put('Telephone', client.contact_info?.phone);
    put('Address', client.contact_info?.address);
    put('Street', client.contact_info?.address);
    put('Mailing Address', client.contact_info?.address);
  }

  // Jurisdiction / Court
  if (matter.jurisdiction_code) {
    put('Jurisdiction', matter.jurisdiction_code);
    put('Court Jurisdiction', matter.jurisdiction_code);
  }

  // Dates
  const dates = matter.important_dates;
  if (dates) {
    const fmt = (d: string | null | undefined) => {
      if (!d) return undefined;
      try { return new Date(d).toLocaleDateString('en-US'); } catch { return d; }
    };
    put('Filing Date', fmt(dates.filing_date));
    put('Date Filed', fmt(dates.filing_date));
    put('Petition Filed Date', fmt(dates.petition_filed_date));
    put('Hearing Date', fmt(dates.next_hearing));
    put('Next Hearing', fmt(dates.next_hearing));
    put('Service Date', fmt(dates.service_date));
    put('Court Deadline', fmt(dates.court_deadline));
  }

  return map;
}

/**
 * Attempt to match a field label against the auto-fill map.
 * Tries exact match first, then progressively looser matching.
 */
function resolveAutoFillValue(
  fieldLabel: string,
  autoFillMap: Map<string, string>,
): string | null {
  const normalized = fieldLabel.toLowerCase().replace(/\s*\(page \d+\)$/i, '').trim();

  // Exact match
  if (autoFillMap.has(normalized)) return autoFillMap.get(normalized)!;

  // Try without common suffixes/prefixes
  const withoutSuffix = normalized.replace(/\s+(name|number|no|date|info)$/i, '').trim();
  for (const [key, value] of autoFillMap) {
    const keyBase = key.replace(/\s+(name|number|no|date|info)$/i, '').trim();
    if (keyBase === withoutSuffix) return value;
    // Substring containment: "Plaintiff Name" matches "Plaintiff/Petitioner Name"
    if (normalized.includes(key) || key.includes(normalized)) return value;
  }

  return null;
}

export default function PdfDocumentEditorPage({ docId, versionId, titleFromUrl, onOpenFailure }: Props): React.JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedVersionRef = versionId || searchParams.get('versionId') || searchParams.get('version') || undefined;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [documentTitle, setDocumentTitle] = useState(titleFromUrl || 'PDF Document');
  const [documentVersionId, setDocumentVersionId] = useState<string | null>(requestedVersionRef || null);
  const [lexPanelOpen, setLexPanelOpen] = useState(false);
  const [lexAttachOpen, setLexAttachOpen] = useState(false);
  const [session, setSession] = useState<PdfSessionModel | null>(null);
  const [fields, setFields] = useState<PdfFieldModel[]>([]);
  const [fieldValues, setFieldValues] = useState<Record<string, string | boolean | string[] | null>>({});
  const [validation, setValidation] = useState<PdfValidationModel | null>(null);
  const [search, setSearch] = useState('');
  const [pdfData, setPdfData] = useState<Uint8Array | null>(null);
  const [dirty, setDirty] = useState(false);
  const [overlayMode, setOverlayMode] = useState(false);
  const [overlayFields, setOverlayFields] = useState<OverlayFieldModel[]>([]);
  const [activeOverlayFieldId, setActiveOverlayFieldId] = useState<string | null>(null);
  const [placingOverlayFieldId, setPlacingOverlayFieldId] = useState<string | null>(null);
  const [previewPage, setPreviewPage] = useState(1);
  const [previewPageCount, setPreviewPageCount] = useState(1);
  const [readOnlyMode, setReadOnlyMode] = useState(false);
  const [targetPreviewPage, setTargetPreviewPage] = useState<number | null>(1);
  /** The AcroForm field name currently focused in the sidebar — drives the highlight overlay. */
  const [focusedAcroFieldName, setFocusedAcroFieldName] = useState<string | null>(null);
  const focusedAcroHighlightTimerRef = useRef<number | null>(null);
  const [signatureEvidenceSummary, setSignatureEvidenceSummary] = useState<SignatureEvidenceSummaryModel | null>(null);
  const [signatureEvidenceLoading, setSignatureEvidenceLoading] = useState(false);
  const [signatureEvidenceDownloading, setSignatureEvidenceDownloading] = useState(false);
  const [signaturePresets, setSignaturePresets] = useState<SignaturePresetModel[]>([]);
  const [activeSignaturePresetId, setActiveSignaturePresetId] = useState<string | null>(null);
  const [activeInitialPresetId, setActiveInitialPresetId] = useState<string | null>(null);
  const [showPresetModal, setShowPresetModal] = useState(false);
  const [presetKind, setPresetKind] = useState<SignaturePresetKind>('signature');
  const [presetMode, setPresetMode] = useState<SignaturePresetMode>('draw');
  const [presetLabel, setPresetLabel] = useState('My Signature');
  const [typedPresetValue, setTypedPresetValue] = useState('');
  const [typedPresetStyle, setTypedPresetStyle] = useState<SignatureTypedStyle>('great_vibes');
  const [uploadedPresetDataUrl, setUploadedPresetDataUrl] = useState<string | null>(null);
  const [drawnPresetDataUrl, setDrawnPresetDataUrl] = useState<string | null>(null);
  const [isDrawingPreset, setIsDrawingPreset] = useState(false);
  const [presetPreviewError, setPresetPreviewError] = useState<string | null>(null);

  // Enhancement C: Matter context auto-fill state
  const [matterContext, setMatterContext] = useState<MatterContext | null>(null);
  const [autoFillLoading, setAutoFillLoading] = useState(false);
  const [autoFillResult, setAutoFillResult] = useState<AutoFillResult | null>(null);
  /** The matter_id extracted from the document, set during loadEditorState. */
  const documentMatterIdRef = useRef<string | null>(null);

  const pdfDocRef = useRef<PDFDocument | null>(null);
  const basePdfSnapshotRef = useRef<string | null>(null);
  const fieldRefMap = useRef<Map<string, PDFField>>(new Map());
  const fieldControlRefMap = useRef<Map<string, HTMLInputElement | HTMLSelectElement>>(new Map());
  const previewTimerRef = useRef<number | null>(null);
  const loadCycleRef = useRef(0);
  const sessionIsActive = session?.status === 'active';
  const presetCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const presetDrawingRef = useRef(false);
  const lexMessages = useLexRuntimeStore((state) => state.messages);
  const lexLoading = useLexRuntimeStore((state) => state.isLoading);
  const lexAttachedDocuments = useLexRuntimeStore((state) => state.attachedDocuments);
  const sendLexMessage = useLexRuntimeStore((state) => state.sendMessage);
  const attachLexDmsDocument = useLexRuntimeStore((state) => state.attachDmsDocument);
  const attachLexLocalFiles = useLexRuntimeStore((state) => state.attachLocalFiles);
  const removeLexAttachment = useLexRuntimeStore((state) => state.removeAttachedDocument);
  const sendLexDocumentAction = useLexRuntimeStore((state) => state.sendDocumentAction);
  const setLexDocumentContextFromEditor = useLexRuntimeStore((state) => state.setDocumentContextFromEditor);

  useEffect(() => {
    const loaded = deserializeSignaturePresets(localStorage.getItem(SIGNATURE_PRESET_STORAGE_KEY));
    setSignaturePresets(loaded);
    const firstSignature = loaded.find((preset) => preset.kind === 'signature');
    const firstInitial = loaded.find((preset) => preset.kind === 'initial');
    setActiveSignaturePresetId(firstSignature?.id || null);
    setActiveInitialPresetId(firstInitial?.id || null);
  }, []);

  useEffect(() => {
    localStorage.setItem(SIGNATURE_PRESET_STORAGE_KEY, serializeSignaturePresets(signaturePresets));
  }, [signaturePresets]);

  useEffect(() => {
    let cancelled = false;
    const hydrateTypedPresets = async () => {
      const pending = signaturePresets.filter((preset) =>
        preset.mode === 'type' && !preset.data_url && (preset.typed_value || '').trim().length > 0
      );
      if (pending.length === 0) return;

      const updates = new Map<string, string>();
      for (const preset of pending) {
        const dataUrl = await buildTypedSignatureDataUrl(
          preset.typed_value || '',
          preset.typed_style,
          preset.kind,
        );
        if (dataUrl) {
          updates.set(preset.id, dataUrl);
        }
      }
      if (cancelled || updates.size === 0) return;
      setSignaturePresets((previous) =>
        previous.map((preset) =>
          updates.has(preset.id)
            ? { ...preset, data_url: updates.get(preset.id) || preset.data_url }
            : preset
        ),
      );
    };
    void hydrateTypedPresets();
    return () => {
      cancelled = true;
    };
  }, [signaturePresets]);

  const signaturePresetOptions = useMemo(
    () => signaturePresets.filter((preset) => preset.kind === 'signature'),
    [signaturePresets],
  );
  const initialPresetOptions = useMemo(
    () => signaturePresets.filter((preset) => preset.kind === 'initial'),
    [signaturePresets],
  );

  const activeSignaturePreset = useMemo(
    () => signaturePresetOptions.find((preset) => preset.id === activeSignaturePresetId) || null,
    [signaturePresetOptions, activeSignaturePresetId],
  );
  const activeInitialPreset = useMemo(
    () => initialPresetOptions.find((preset) => preset.id === activeInitialPresetId) || null,
    [initialPresetOptions, activeInitialPresetId],
  );

  useEffect(() => {
    if (!lexPanelOpen || !documentVersionId) return;
    setLexDocumentContextFromEditor({
      documentId: docId,
      versionId: documentVersionId,
      title: documentTitle,
      matterId: documentMatterIdRef.current,
    });
  }, [docId, documentTitle, documentVersionId, lexPanelOpen, setLexDocumentContextFromEditor]);

  useEffect(() => {
    if (!showPresetModal || presetMode !== 'draw') return;
    const canvas = presetCanvasRef.current;
    if (!canvas) return;
    canvas.width = 420;
    canvas.height = 140;
    const context = canvas.getContext('2d');
    if (!context) return;
    context.lineJoin = 'round';
    context.lineCap = 'round';
    context.lineWidth = 2.2;
    context.strokeStyle = '#111827';
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
  }, [showPresetModal, presetMode]);

  useEffect(() => {
    if (!showPresetModal || presetMode !== 'type') return;
    void ensureSignatureWebFont(typedPresetStyle);
  }, [presetMode, showPresetModal, typedPresetStyle]);

  const serializePdf = useCallback(async (): Promise<Uint8Array> => {
    if (!pdfDocRef.current) {
      throw new Error('PDF is not loaded.');
    }
    try {
      return await pdfDocRef.current.save();
    } catch {
      // Fallback for structurally noisy court PDFs where appearance generation can fail.
      return pdfDocRef.current.save({ updateFieldAppearances: false, useObjectStreams: false });
    }
  }, []);

  const loadReadOnlyPdfBytes = useCallback(async (): Promise<Uint8Array> => {
    const documentResponse = await apiClient.get(`/dms/documents/${docId}?versions=all`);
    const documentData = documentResponse.data?.document || documentResponse.data || {};
    const resolvedVersionId = resolveRequestedVersionId(documentData, requestedVersionRef);

    if (!resolvedVersionId) {
      throw new Error('No document version is available for PDF preview.');
    }

    const pdfResponse = await apiClient.get(
      `/dms/documents/${docId}/versions/${encodeURIComponent(resolvedVersionId)}/download`,
      { responseType: 'arraybuffer' },
    );
    return new Uint8Array(pdfResponse.data);
  }, [docId, requestedVersionRef]);

  const buildOverlayPdfBytes = useCallback(async (
    providedFields?: OverlayFieldModel[],
    providedValues?: Record<string, PdfFieldValue>,
    baseBytesOverride?: Uint8Array,
  ): Promise<Uint8Array> => {
    const fieldsToRender = providedFields || overlayFields;
    const valuesToRender = providedValues || fieldValues;
    if (fieldsToRender.length === 0) {
      if (baseBytesOverride) {
        return baseBytesOverride.slice(0);
      }
      const baseSnapshot = basePdfSnapshotRef.current;
      if (!baseSnapshot) {
        throw new Error('Base PDF is not loaded.');
      }
      return base64ToUint8(baseSnapshot);
    }

    if (baseBytesOverride) {
      return renderOverlayPdf(baseBytesOverride, fieldsToRender, valuesToRender);
    }

    const baseSnapshot = basePdfSnapshotRef.current;
    if (!baseSnapshot) {
      throw new Error('Base PDF is not loaded.');
    }
    // Render from an immutable base snapshot for non-Acro overlay-only documents.
    return renderOverlayPdf(base64ToUint8(baseSnapshot), fieldsToRender, valuesToRender);
  }, [fieldValues, overlayFields]);

  const serializeWorkingPdf = useCallback(async (): Promise<Uint8Array> => {
    if (overlayMode) {
      return buildOverlayPdfBytes();
    }
    const base = await serializePdf();
    if (overlayFields.length === 0) {
      return base;
    }
    return buildOverlayPdfBytes(undefined, undefined, base);
  }, [buildOverlayPdfBytes, overlayFields.length, overlayMode, serializePdf]);

  const serializeWorkingPdfForPersistence = useCallback(async (): Promise<Uint8Array> => {
    try {
      return await serializeWorkingPdf();
    } catch (serializationError) {
      if ((overlayMode || overlayFields.length > 0) && hasPdfHeader(pdfData)) {
        // Persist the current viewport PDF bytes as a resilient fallback.
        return pdfData.slice(0);
      }
      if (serializationError instanceof Error) {
        throw serializationError;
      }
      throw new Error('Failed to serialize the current PDF state.');
    }
  }, [overlayFields.length, overlayMode, pdfData, serializeWorkingPdf]);

  const serializeDraftPdfForPersistence = useCallback(async (): Promise<Uint8Array> => {
    if (overlayFields.length === 0) {
      return serializeWorkingPdfForPersistence();
    }
    // Keep overlays editable across drafts by persisting the non-flattened PDF bytes.
    if (overlayMode) {
      const baseSnapshot = basePdfSnapshotRef.current;
      if (!baseSnapshot) {
        throw new Error('Base PDF is not loaded.');
      }
      return base64ToUint8(baseSnapshot);
    }
    // Acro PDF with overlay signatures: persist Acro edits, keep overlay stamps virtual until commit.
    return serializePdf();
  }, [overlayFields.length, overlayMode, serializePdf, serializeWorkingPdfForPersistence]);

  const buildFieldValuesPayload = useCallback((includeOverlaySchema: boolean): Record<string, PdfFieldValue> => {
    const payload: Record<string, PdfFieldValue> = {
      ...fieldValues,
    };

    if (overlayFields.length > 0) {
      for (const field of overlayFields) {
        const value = fieldValues[field.name];
        let normalizedValue: PdfFieldValue;
        if (field.type === 'checkbox') {
          normalizedValue = Boolean(value);
        } else if (Array.isArray(value)) {
          normalizedValue = value.join(', ');
        } else if (typeof value === 'string') {
          normalizedValue = value;
        } else if (value === null || value === undefined) {
          normalizedValue = '';
        } else {
          normalizedValue = String(value);
        }
        payload[field.name] = normalizedValue;

        // Add semantic alias keys from labels to improve readiness validation patterns.
        const semanticAlias = `overlay_label.${slugifyFieldLabel(field.label)}.${field.id.slice(0, 8)}`;
        payload[semanticAlias] = normalizedValue;
      }
    }

    if (includeOverlaySchema || overlayFields.length > 0) {
      payload[OVERLAY_SCHEMA_KEY] = serializeOverlaySchema(overlayFields);
    }
    return payload;
  }, [fieldValues, overlayFields]);

  const hydrateSignatureEvidenceSummary = useCallback(async (
    baseSummary: SignatureEvidenceSummaryModel,
  ): Promise<SignatureEvidenceSummaryModel> => {
    if (!baseSummary.exportEndpoint) {
      return {
        ...baseSummary,
        canDownload: false,
      };
    }

    try {
      const response = await apiClient.get(baseSummary.exportEndpoint, {
        params: {
          download: 'false',
          includeEventData: 'false',
        },
      });
      const evidence = response.data?.evidence;
      const signerNames = parseEvidenceSigners(evidence);
      const latestSignedAt = parseLatestSignedAt(evidence);
      return {
        ...baseSummary,
        signerNames: signerNames.length > 0 ? signerNames : baseSummary.signerNames,
        latestSignedAt,
        canDownload: true,
        accessDenied: false,
      };
    } catch (error) {
      const status = getApiErrorStatus(error);
      return {
        ...baseSummary,
        canDownload: status !== 403,
        accessDenied: status === 403,
      };
    }
  }, []);

  const applySignatureEvidenceSummaryFromVersion = useCallback(async (versionCandidate: unknown) => {
    const baseSummary = parseSignatureEvidenceSummaryFromVersion(versionCandidate);
    setSignatureEvidenceSummary(baseSummary);
    if (!baseSummary) {
      return;
    }

    if (!baseSummary.exportEndpoint) {
      return;
    }

    setSignatureEvidenceLoading(true);
    try {
      const hydratedSummary = await hydrateSignatureEvidenceSummary(baseSummary);
      setSignatureEvidenceSummary(hydratedSummary);
    } finally {
      setSignatureEvidenceLoading(false);
    }
  }, [hydrateSignatureEvidenceSummary]);

  const refreshSignatureEvidenceSummary = useCallback(async (targetVersionId?: string | null) => {
    const documentResponse = await apiClient.get(`/dms/documents/${docId}?versions=all`);
    const documentData = documentResponse.data?.document || documentResponse.data || {};
    const versions = Array.isArray(documentData?.versions) ? documentData.versions : [];
    const latestVersion = documentData?.latest_version || documentData?.latestVersion;

    let versionCandidate: unknown = latestVersion;
    if (targetVersionId) {
      versionCandidate = versions.find((version: any) =>
        version?.version_id === targetVersionId || version?.id === targetVersionId,
      ) || latestVersion;
    }

    await applySignatureEvidenceSummaryFromVersion(versionCandidate || {});
  }, [applySignatureEvidenceSummaryFromVersion, docId]);

  const downloadSignatureEvidence = useCallback(async () => {
    if (!signatureEvidenceSummary?.exportEndpoint || !signatureEvidenceSummary.canDownload) {
      return;
    }

    setSignatureEvidenceDownloading(true);
    setError(null);
    try {
      const response = await apiClient.get(signatureEvidenceSummary.exportEndpoint, {
        responseType: 'arraybuffer',
      });
      const fallbackName = `signature-evidence-${docId}-${signatureEvidenceSummary.sessionId || 'session'}.json`;
      const fileName = extractDownloadFileName(response.headers?.['content-disposition'], fallbackName);
      const blob = new Blob([response.data], { type: 'application/json;charset=utf-8' });
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (downloadError) {
      const status = getApiErrorStatus(downloadError);
      if (status === 403) {
        setSignatureEvidenceSummary((previous) => previous ? {
          ...previous,
          canDownload: false,
          accessDenied: true,
        } : previous);
        setInfoMessage('Evidence download is restricted to the signature session owner or a firm admin.');
      } else {
        setError(getApiErrorMessage(downloadError, 'Failed to download evidence package.'));
      }
    } finally {
      setSignatureEvidenceDownloading(false);
    }
  }, [docId, signatureEvidenceSummary]);

  /** Navigate the PDF viewer to the page/location of an AcroForm field when focused. */
  const handleAcroFieldFocus = useCallback((fieldName: string) => {
    const model = fields.find((f) => f.name === fieldName);
    if (!model) return;
    // Jump to page
    if (model.page && model.page > 0) {
      setTargetPreviewPage(null);
      // Force a state cycle so PdfViewer picks up the new page even if already on it
      window.setTimeout(() => setTargetPreviewPage(model.page!), 0);
    }
    // Show highlight
    setFocusedAcroFieldName(fieldName);
    // Auto-clear highlight after 3 seconds
    if (focusedAcroHighlightTimerRef.current) {
      window.clearTimeout(focusedAcroHighlightTimerRef.current);
    }
    focusedAcroHighlightTimerRef.current = window.setTimeout(() => {
      setFocusedAcroFieldName(null);
      focusedAcroHighlightTimerRef.current = null;
    }, 3000);
  }, [fields]);

  /** Clear the AcroForm highlight when the field loses focus. */
  const handleAcroFieldBlur = useCallback(() => {
    // Delay clear so highlight is briefly visible after blur
    if (focusedAcroHighlightTimerRef.current) {
      window.clearTimeout(focusedAcroHighlightTimerRef.current);
    }
    focusedAcroHighlightTimerRef.current = window.setTimeout(() => {
      setFocusedAcroFieldName(null);
      focusedAcroHighlightTimerRef.current = null;
    }, 800);
  }, []);

  /** Build a temporary highlight overlay for the focused AcroForm field. */
  const acroFieldHighlightOverlay = useMemo<PdfViewerOverlay | null>(() => {
    if (!focusedAcroFieldName) return null;
    const model = fields.find((f) => f.name === focusedAcroFieldName);
    if (!model?.rect || !model.page) return null;
    return {
      id: `__acro_highlight__${model.name}`,
      page: model.page,
      x: model.rect.x,
      y: model.rect.y,
      width: model.rect.width,
      height: model.rect.height,
      label: model.label,
    };
  }, [fields, focusedAcroFieldName]);

  const overlayPreviewItems = useMemo<PdfViewerOverlay[]>(() => {
    const items: PdfViewerOverlay[] = [];
    // Add AcroForm field highlight if active
    if (acroFieldHighlightOverlay) {
      items.push(acroFieldHighlightOverlay);
    }
    // Add overlay fields
    for (const field of overlayFields) {
      items.push({
        id: field.id,
        page: field.page,
        x: field.x,
        y: field.y,
        width: field.width,
        height: field.height,
        label: field.label,
      });
    }
    return items;
  }, [acroFieldHighlightOverlay, overlayFields]);

  const loadEditorState = useCallback(async () => {
    const loadCycleId = loadCycleRef.current + 1;
    loadCycleRef.current = loadCycleId;
    const isStale = () => loadCycleRef.current !== loadCycleId;

    setLoading(true);
    setError(null);
    setInfoMessage(null);
    setValidation(null);
    setPdfData(null);
    setFields([]);
    setFieldValues({});
    setSession(null);
    setOverlayMode(false);
    setOverlayFields([]);
    setDocumentVersionId(requestedVersionRef || null);
    setActiveOverlayFieldId(null);
    setPlacingOverlayFieldId(null);
    setReadOnlyMode(false);
    setTargetPreviewPage(1);
    setSignatureEvidenceSummary(null);
    setSignatureEvidenceLoading(false);
    setSignatureEvidenceDownloading(false);
    basePdfSnapshotRef.current = null;

    try {
      const documentResponse = await apiClient.get(`/dms/documents/${docId}?versions=all`);
      if (isStale()) return;
      const documentData = documentResponse.data?.document || documentResponse.data || {};
      const resolvedVersionId = resolveRequestedVersionId(documentData, requestedVersionRef);
      const resolvedTitle = titleFromUrl || documentData.title || 'PDF Document';
      setDocumentTitle(resolvedTitle);
      setDocumentVersionId(resolvedVersionId || null);
      // Enhancement C: Capture matter association for auto-fill
      documentMatterIdRef.current = documentData.matter_id || null;

      const versionCandidate = (Array.isArray(documentData.versions) ? documentData.versions : []).find((version: any) =>
        version?.version_id === resolvedVersionId || version?.id === resolvedVersionId,
      ) || documentData.latest_version || documentData.latestVersion || {};
      const mimeType = String(versionCandidate?.mime_type || '').toLowerCase();
      const originalName = String(versionCandidate?.original_name || '').toLowerCase();
      const isPdf = mimeType.includes('application/pdf') || originalName.endsWith('.pdf');
      if (!isPdf) {
        throw new Error('This document is not a PDF. Use the standard editor.');
      }

      void applySignatureEvidenceSummaryFromVersion(versionCandidate);

      const startSession = async (): Promise<PdfSessionModel> => {
        const payload = resolvedVersionId ? { version_id: resolvedVersionId } : {};
        try {
          const sessionResponse = await apiClient.post(`/dms/documents/${docId}/pdf/sessions`, payload);
          const startedSession: PdfSessionModel = sessionResponse.data?.session;
          if (!startedSession?.session_id) {
            throw new Error('Failed to create a PDF edit session.');
          }
          return startedSession;
        } catch (sessionError) {
          if (getApiErrorStatus(sessionError) !== 409) {
            throw sessionError;
          }

          await apiClient.post(`/dms/documents/${docId}/checkout`, {
            durationHours: 24,
            sessionId: `pdf_editor_${Date.now()}`,
            deviceInfo: typeof navigator !== 'undefined' ? navigator.userAgent : 'web',
          });

          const retryResponse = await apiClient.post(`/dms/documents/${docId}/pdf/sessions`, payload);
          const retriedSession: PdfSessionModel = retryResponse.data?.session;
          if (!retriedSession?.session_id) {
            throw new Error('Failed to create a PDF edit session after checkout.');
          }
          setInfoMessage('Document was automatically checked out for PDF editing.');
          return retriedSession;
        }
      };

      const startedSession = await startSession();
      if (isStale()) return;
      if (!startedSession?.session_id) {
        throw new Error('Failed to create a PDF edit session.');
      }
      setSession(startedSession);

      const pdfResponse = await apiClient.get(`/dms/documents/${docId}/pdf`, {
        params: { sessionId: startedSession.session_id },
        responseType: 'arraybuffer',
      });
      if (isStale()) return;
      const initialBytes = new Uint8Array(pdfResponse.data);
      const baseBytes = initialBytes.slice(0);
      basePdfSnapshotRef.current = uint8ToBase64(baseBytes);
      // Defer raw bytes display — if overlay fields exist, renderOverlayPdf below will
      // produce a normalized (pdf-lib saved) version that pdfjs renders more reliably.
      // Showing raw bytes here first causes a brief "Unable to render PDF page" flash on
      // complex PDFs before the overlay bytes arrive.

      const pdfDoc = await PDFDocument.load(baseBytes.slice(0), { ignoreEncryption: true });
      pdfDocRef.current = pdfDoc;

      const fieldModels: PdfFieldModel[] = [];
      const values: Record<string, string | boolean | string[] | null> = {};
      fieldRefMap.current = new Map();
      const pageRefMap = buildPageRefMap(pdfDoc);

      try {
        const form = pdfDoc.getForm();
        const pdfFields = form.getFields();
        for (const field of pdfFields) {
          const name = field.getName();
          const type = inferFieldType(field);
          const fieldPage = resolveFieldPage(field, pageRefMap);
          const fieldRect = resolveFieldRect(field, pdfDoc, pageRefMap);
          const model: PdfFieldModel = {
            name,
            label: normalizeFieldLabel(name),
            type,
            page: fieldPage ?? undefined,
            required: field.isRequired(),
            rect: fieldRect ?? undefined,
          };
          if (field instanceof PDFDropdown || field instanceof PDFOptionList || field instanceof PDFRadioGroup) {
            model.options = field.getOptions();
          }
          fieldModels.push(model);
          fieldRefMap.current.set(name, field);
          values[name] = readFieldValue(field);
        }
      } catch {
        // No AcroForm available; keep empty fields list.
      }

      // Add page suffixes only where labels are ambiguous
      disambiguateFieldLabels(fieldModels);

      const sessionValues = { ...(startedSession.field_values || {}) } as Record<string, PdfFieldValue>;
      const overlaySchema = parseOverlaySchema(sessionValues[OVERLAY_SCHEMA_KEY]);
      delete sessionValues[OVERLAY_SCHEMA_KEY];

      for (const [name, value] of Object.entries(sessionValues)) {
        const field = fieldRefMap.current.get(name);
        if (!field) continue;
        try {
          setFieldValue(field, value);
          values[name] = value;
        } catch {
          // Ignore malformed persisted values that no longer map to the field definition.
        }
      }

      setFields(fieldModels);
      const nextOverlayMode = fieldModels.length === 0;
      setOverlayMode(nextOverlayMode);
      const currentDocSlug = buildDocSlug(resolvedTitle);

      let normalizedOverlayFields = overlaySchema;
      if (normalizedOverlayFields.length === 0) {
        const legacyOverlayKeys = Object.keys(sessionValues).filter((key) => key.startsWith('overlay.'));
        const existingNames = new Set<string>();
        normalizedOverlayFields = legacyOverlayKeys.map((key, index) => {
          const field = sanitizeOverlayField(
            {
              id: createOverlayId(),
              name: key,
              label: key.split('.').slice(1, 3).join(' ').replace(/_/g, ' ') || `Field ${index + 1}`,
              type: typeof sessionValues[key] === 'boolean' ? 'checkbox' : 'text',
              page: 1,
              x: 64,
              y: 64 + (index % 12) * 28,
              width: 220,
              height: 20,
              font_size: 11,
            },
            index + 1,
            currentDocSlug,
            existingNames,
          );
          existingNames.add(field.name);
          return field;
        });
      }
      setOverlayFields(normalizedOverlayFields);
      setActiveOverlayFieldId(normalizedOverlayFields[0]?.id || null);

      const combinedValues: Record<string, PdfFieldValue> = { ...values };
      for (const overlayField of normalizedOverlayFields) {
        const persistedValue = sessionValues[overlayField.name];
        combinedValues[overlayField.name] = persistedValue ?? (overlayField.type === 'checkbox' ? false : '');
      }
      setFieldValues(combinedValues);

      if (normalizedOverlayFields.length > 0) {
        const baseForOverlay = nextOverlayMode
          ? baseBytes.slice(0)
          : await serializePdf();
        const overlayPreview = await renderOverlayPdf(baseForOverlay, normalizedOverlayFields, combinedValues);
        if (isStale()) return;
        setPdfData(overlayPreview.slice(0));
      } else {
        // No overlay fields — raw bytes are the final output; safe to display directly.
        setPdfData(baseBytes.slice(0));
      }
      setDirty(false);
    } catch (loadError) {
      const status = getApiErrorStatus(loadError);
      const message = getApiErrorMessage(loadError, 'Failed to open PDF editor.');

      if (isReadOnlyPdfFallbackError(status, message)) {
        try {
          const readOnlyBytes = await loadReadOnlyPdfBytes();
          setPdfData(readOnlyBytes.slice(0));
          setSession(null);
          setFields([]);
          setFieldValues({});
          setDirty(false);
          setValidation(null);
          setReadOnlyMode(true);
          setError(null);
          setInfoMessage('This PDF has no editable form fields. Opened in read-only preview mode.');
          return;
        } catch (fallbackError) {
          const fallbackMessage = getApiErrorMessage(fallbackError, message);
          setError(fallbackMessage);
          onOpenFailure?.({ status, message: fallbackMessage });
          return;
        }
      }

      setError(message);
      onOpenFailure?.({ status, message });
    } finally {
      if (!isStale()) {
        setLoading(false);
      }
    }
  }, [applySignatureEvidenceSummaryFromVersion, docId, loadReadOnlyPdfBytes, onOpenFailure, requestedVersionRef, serializePdf, titleFromUrl]);

  useEffect(() => {
    void loadEditorState();
    return () => {
      loadCycleRef.current += 1;
      if (previewTimerRef.current) {
        window.clearTimeout(previewTimerRef.current);
      }
    };
  }, [loadEditorState]);

  const cancelPendingPreview = useCallback(() => {
    if (previewTimerRef.current) {
      window.clearTimeout(previewTimerRef.current);
      previewTimerRef.current = null;
    }
  }, []);

  const updatePreview = useCallback((delayMs = 250) => {
    cancelPendingPreview();
    previewTimerRef.current = window.setTimeout(async () => {
      try {
        if (overlayMode) {
          const overlayBytes = await buildOverlayPdfBytes();
          setPdfData(overlayBytes.slice(0));
          return;
        }
        if (!pdfDocRef.current) return;
        const acroBytes = await serializePdf();
        if (overlayFields.length === 0) {
          setPdfData(acroBytes.slice(0));
          return;
        }
        const mergedBytes = await buildOverlayPdfBytes(undefined, undefined, acroBytes);
        setPdfData(mergedBytes.slice(0));
      } catch (previewError) {
        setError(getApiErrorMessage(previewError, 'Failed to render PDF preview.'));
      }
    }, delayMs);
  }, [buildOverlayPdfBytes, cancelPendingPreview, overlayFields.length, overlayMode, serializePdf]);

  const updateField = useCallback((
    name: string,
    value: string | boolean | string[] | null,
    options?: { refreshPreview?: boolean },
  ) => {
    const isOverlayField = overlayFields.some((field) => field.name === name);
    if (overlayMode || isOverlayField) {
      setFieldValues((prev) => ({ ...prev, [name]: value }));
      setDirty(true);
      setInfoMessage(null);
      setValidation(null);
      if (options?.refreshPreview !== false) {
        updatePreview();
      }
      return;
    }

    const field = fieldRefMap.current.get(name);
    if (!field) return;
    setFieldValue(field, value);
    setFieldValues((prev) => ({ ...prev, [name]: value }));
    setDirty(true);
    setInfoMessage(null);
    setValidation(null);
    if (options?.refreshPreview !== false) {
      updatePreview();
    }
  }, [overlayFields, overlayMode, updatePreview]);

  const registerFieldControlRef = useCallback((
    fieldName: string,
    control: HTMLInputElement | HTMLSelectElement | null,
  ) => {
    if (control) {
      fieldControlRefMap.current.set(fieldName, control);
      return;
    }
    fieldControlRefMap.current.delete(fieldName);
  }, []);

  const resetPresetEditor = useCallback((kind: SignaturePresetKind) => {
    setPresetKind(kind);
    setPresetMode('draw');
    setPresetLabel(kind === 'signature' ? 'My Signature' : 'My Initial');
    setTypedPresetValue('');
    setTypedPresetStyle('great_vibes');
    setUploadedPresetDataUrl(null);
    setDrawnPresetDataUrl(null);
    setPresetPreviewError(null);
  }, []);

  const openPresetModal = useCallback((kind: SignaturePresetKind) => {
    resetPresetEditor(kind);
    setShowPresetModal(true);
  }, [resetPresetEditor]);

  const extractPresetValue = useCallback((preset: SignaturePresetModel): string => {
    if (preset.data_url) {
      return preset.data_url;
    }
    return preset.typed_value || '';
  }, []);

  const applyPresetToOverlayField = useCallback((fieldId: string, preset: SignaturePresetModel | null) => {
    if (!preset) return;
    setOverlayFields((previous) => {
      return previous.map((field) => {
        if (field.id !== fieldId) return field;
        return sanitizeOverlayField({
          ...field,
          signature_preset_id: preset.id,
          signature_style: preset.typed_style,
          type: preset.kind,
        }, 1);
      });
    });
    const targetField = overlayFields.find((field) => field.id === fieldId);
    if (targetField) {
      setFieldValues((previous) => ({
        ...previous,
        [targetField.name]: extractPresetValue(preset),
      }));
    }
    setDirty(true);
    setValidation(null);
    void updatePreview(80);
  }, [extractPresetValue, overlayFields, updatePreview]);

  const createOverlayFieldFromPreset = useCallback((preset: SignaturePresetModel) => {
    const nextType: OverlayFieldType = preset.kind;
    const docSlug = buildDocSlug(documentTitle);
    setOverlayFields((previous) => {
      const existingNames = new Set(previous.map((f) => f.name));
      const created = createDefaultOverlayFieldByType(previous.length + 1, previewPage, nextType, {
        label: preset.kind === 'signature' ? 'Signature' : 'Initial',
        docSlug,
        existingNames,
      });
      created.signature_preset_id = preset.id;
      created.signature_style = preset.typed_style;
      const next = [...previous, created];
      setActiveOverlayFieldId(created.id);
      setPlacingOverlayFieldId(created.id);
      setFieldValues((values) => ({
        ...values,
        [created.name]: extractPresetValue(preset),
      }));
      return next;
    });
    setTargetPreviewPage(previewPage);
    setDirty(true);
    setValidation(null);
    setInfoMessage('Click inside the PDF to place the selected signature.');
    void updatePreview(80);
  }, [documentTitle, extractPresetValue, previewPage, updatePreview]);

  const onPresetUploadChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const value = typeof reader.result === 'string' ? reader.result : null;
      setUploadedPresetDataUrl(value);
    };
    reader.readAsDataURL(file);
  }, []);

  const handleSavePreset = useCallback(async () => {
    const label = presetLabel.trim() || (presetKind === 'signature' ? 'Signature' : 'Initial');
    let dataUrl: string | null = null;
    let typedValue: string | null = null;
    if (presetMode === 'draw') {
      dataUrl = drawnPresetDataUrl;
      if (!dataUrl) {
        setPresetPreviewError('Draw a signature before saving.');
        return;
      }
    } else if (presetMode === 'upload') {
      dataUrl = uploadedPresetDataUrl;
      if (!dataUrl) {
        setPresetPreviewError('Upload a signature image before saving.');
        return;
      }
    } else {
      typedValue = typedPresetValue.trim();
      if (!typedValue) {
        setPresetPreviewError('Type a signature value before saving.');
        return;
      }
      dataUrl = await buildTypedSignatureDataUrl(typedValue, typedPresetStyle, presetKind);
      if (!dataUrl) {
        setPresetPreviewError('Unable to render typed signature. Try draw or upload.');
        return;
      }
    }

    const preset: SignaturePresetModel = {
      id: createOverlayId(),
      kind: presetKind,
      mode: presetMode,
      label,
      data_url: dataUrl,
      typed_value: typedValue,
      typed_style: presetMode === 'type' ? typedPresetStyle : null,
      created_at: new Date().toISOString(),
    };

    setSignaturePresets((previous) => [preset, ...previous]);
    if (preset.kind === 'signature') {
      setActiveSignaturePresetId(preset.id);
    } else {
      setActiveInitialPresetId(preset.id);
    }
    setShowPresetModal(false);
    setInfoMessage(`${preset.kind === 'signature' ? 'Signature' : 'Initial'} preset saved.`);
  }, [
    drawnPresetDataUrl,
    presetKind,
    presetLabel,
    presetMode,
    typedPresetStyle,
    typedPresetValue,
    uploadedPresetDataUrl,
  ]);

  const clearDrawnPresetCanvas = useCallback(() => {
    const canvas = presetCanvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext('2d');
    if (!context) return;
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    setDrawnPresetDataUrl(null);
  }, []);

  const pointerPositionForCanvas = useCallback((event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = event.currentTarget;
    const rect = canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  }, []);

  const handlePresetCanvasPointerDown = useCallback((event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = presetCanvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext('2d');
    if (!context) return;
    const { x, y } = pointerPositionForCanvas(event);
    context.beginPath();
    context.moveTo(x, y);
    presetDrawingRef.current = true;
    setIsDrawingPreset(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  }, [pointerPositionForCanvas]);

  const handlePresetCanvasPointerMove = useCallback((event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!presetDrawingRef.current) return;
    const canvas = presetCanvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext('2d');
    if (!context) return;
    const { x, y } = pointerPositionForCanvas(event);
    context.lineTo(x, y);
    context.stroke();
  }, [pointerPositionForCanvas]);

  const finishPresetCanvasStroke = useCallback(() => {
    const canvas = presetCanvasRef.current;
    if (!canvas) return;
    if (!presetDrawingRef.current) return;
    presetDrawingRef.current = false;
    setIsDrawingPreset(false);
    setDrawnPresetDataUrl(canvas.toDataURL('image/png'));
  }, []);

  const jumpToValidationIssue = useCallback((issue: FilingIssueModel) => {
    const hintedPage = extractPageHintFromIssue(issue);
    if (hintedPage) {
      setTargetPreviewPage(null);
      window.setTimeout(() => {
        setTargetPreviewPage(hintedPage);
      }, 0);
    }

    if (!issue.field_name) {
      return;
    }

    setSearch('');

    let focusFieldName = issue.field_name;
    if (focusFieldName.startsWith('overlay_label.')) {
      const suffix = focusFieldName.split('.').pop() || '';
      const matchedOverlayField = overlayFields.find((field) => field.id.startsWith(suffix));
      if (matchedOverlayField) {
        focusFieldName = matchedOverlayField.name;
        setTargetPreviewPage(matchedOverlayField.page);
      }
    }

    const tryFocus = (): boolean => {
      const control = fieldControlRefMap.current.get(focusFieldName || '');
      if (!control) {
        return false;
      }
      control.scrollIntoView({ behavior: 'smooth', block: 'center' });
      control.focus();
      return true;
    };

    if (tryFocus()) {
      return;
    }

    window.setTimeout(() => {
      tryFocus();
    }, 120);
  }, [overlayFields]);

  const upsertOverlayField = useCallback((
    fieldId: string,
    patch: Partial<OverlayFieldModel>,
    options?: { refreshPreview?: boolean },
  ) => {
    setOverlayFields((previous) => {
      const next = previous.map((field) => {
        if (field.id !== fieldId) {
          return field;
        }
        return sanitizeOverlayField({ ...field, ...patch }, 1);
      });
      return next;
    });
    setDirty(true);
    setValidation(null);
    setInfoMessage(null);
    if (options?.refreshPreview !== false) {
      void updatePreview(80);
    }
  }, [updatePreview]);

  const addOverlayField = useCallback(() => {
    const docSlug = buildDocSlug(documentTitle);
    setOverlayFields((previous) => {
      const existingNames = new Set(previous.map((f) => f.name));
      const next = [...previous, createDefaultOverlayField(previous.length + 1, previewPage, docSlug, existingNames)];
      const created = next[next.length - 1];
      setActiveOverlayFieldId(created.id);
      setFieldValues((values) => ({
        ...values,
        [created.name]: created.type === 'checkbox' ? false : '',
      }));
      return next;
    });
    setDirty(true);
    setValidation(null);
    setInfoMessage(null);
    void updatePreview(80);
  }, [documentTitle, previewPage, updatePreview]);

  const addSignatureOrInitialField = useCallback((kind: SignaturePresetKind) => {
    const preset = kind === 'signature' ? activeSignaturePreset : activeInitialPreset;
    const docSlug = buildDocSlug(documentTitle);
    setOverlayFields((previous) => {
      const existingNames = new Set(previous.map((f) => f.name));
      const created = createDefaultOverlayFieldByType(previous.length + 1, previewPage, kind, {
        docSlug,
        existingNames,
      });
      if (preset) {
        created.signature_preset_id = preset.id;
        created.signature_style = preset.typed_style;
      }
      const next = [...previous, created];
      setActiveOverlayFieldId(created.id);
      setPlacingOverlayFieldId(created.id);
      setFieldValues((values) => ({
        ...values,
        [created.name]: preset ? extractPresetValue(preset) : '',
      }));
      return next;
    });
    setDirty(true);
    setValidation(null);
    setInfoMessage(preset
      ? `Click inside the PDF to place ${kind}.`
      : `Create a saved ${kind} preset to place a stylized ${kind}.`);
    void updatePreview(80);
  }, [activeInitialPreset, activeSignaturePreset, documentTitle, extractPresetValue, previewPage, updatePreview]);

  const addFreeformTextField = useCallback(() => {
    const docSlug = buildDocSlug(documentTitle);
    setOverlayFields((previous) => {
      const existingNames = new Set(previous.map((f) => f.name));
      const created = createDefaultOverlayFieldByType(previous.length + 1, previewPage, 'freeform_text', {
        docSlug,
        existingNames,
      });
      const next = [...previous, created];
      setActiveOverlayFieldId(created.id);
      setPlacingOverlayFieldId(created.id);
      setFieldValues((values) => ({ ...values, [created.name]: '' }));
      return next;
    });
    setDirty(true);
    setValidation(null);
    setInfoMessage('Click inside the PDF to place the text annotation.');
    void updatePreview(80);
  }, [documentTitle, previewPage, updatePreview]);

  const removeOverlayField = useCallback((fieldId: string) => {
    setOverlayFields((previous) => {
      const target = previous.find((field) => field.id === fieldId);
      if (!target) {
        return previous;
      }
      setFieldValues((values) => {
        const nextValues = { ...values };
        delete nextValues[target.name];
        return nextValues;
      });
      const next = previous.filter((field) => field.id !== fieldId);
      if (activeOverlayFieldId === fieldId) {
        setActiveOverlayFieldId(next[0]?.id || null);
      }
      return next;
    });
    setPlacingOverlayFieldId((previous) => (previous === fieldId ? null : previous));
    setDirty(true);
    setValidation(null);
    setInfoMessage(null);
    void updatePreview(80);
  }, [activeOverlayFieldId, updatePreview]);

  useEffect(() => {
    if (!activeOverlayFieldId) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Delete' && event.key !== 'Backspace') return;
      const target = event.target as HTMLElement | null;
      const tagName = (target?.tagName || '').toLowerCase();
      const isTypingContext =
        tagName === 'input' ||
        tagName === 'textarea' ||
        tagName === 'select' ||
        Boolean(target?.isContentEditable);
      if (isTypingContext) return;
      if (!sessionIsActive || readOnlyMode) return;
      event.preventDefault();
      removeOverlayField(activeOverlayFieldId);
      setInfoMessage('Selected signature field removed.');
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeOverlayFieldId, readOnlyMode, removeOverlayField, sessionIsActive]);

  const beginOverlayPlacement = useCallback((fieldId: string) => {
    setPlacingOverlayFieldId(fieldId);
    const target = overlayFields.find((field) => field.id === fieldId);
    if (target?.page) {
      setTargetPreviewPage(target.page);
    }
  }, [overlayFields]);

  const handleViewerCanvasClick = useCallback((point: { page: number; x: number; y: number }) => {
    if (!placingOverlayFieldId) {
      return;
    }
    upsertOverlayField(placingOverlayFieldId, {
      page: point.page,
      x: Math.max(0, Math.round(point.x)),
      y: Math.max(0, Math.round(point.y)),
    });
    setTargetPreviewPage(point.page);
    setPlacingOverlayFieldId(null);
  }, [placingOverlayFieldId, upsertOverlayField]);

  const handleOverlayPreviewSelect = useCallback((overlayId: string) => {
    setActiveOverlayFieldId(overlayId);
    const selected = overlayFields.find((field) => field.id === overlayId);
    if (!selected) {
      return;
    }
    setTargetPreviewPage(selected.page);
    const control = fieldControlRefMap.current.get(selected.name);
    if (control) {
      control.scrollIntoView({ behavior: 'smooth', block: 'center' });
      control.focus();
    }
    const selectedValue = selected ? fieldValues[selected.name] : null;
    const isEmptySignature =
      (selected?.type === 'signature' || selected?.type === 'initial') &&
      (!selectedValue || (typeof selectedValue === 'string' && selectedValue.trim().length === 0));
    if (isEmptySignature) {
      setInfoMessage('Empty signature box selected. Press Delete/Backspace or click Remove Selected.');
    }
  }, [fieldValues, overlayFields]);

  const handleOverlayPreviewMove = useCallback((overlayId: string, point: { page: number; x: number; y: number }) => {
    upsertOverlayField(
      overlayId,
      {
        page: point.page,
        x: Math.max(0, Math.round(point.x)),
        y: Math.max(0, Math.round(point.y)),
      },
      { refreshPreview: false },
    );
  }, [upsertOverlayField]);

  const handleOverlayPreviewMoveEnd = useCallback((overlayId: string, point: { page: number; x: number; y: number }) => {
    upsertOverlayField(
      overlayId,
      {
        page: point.page,
        x: Math.max(0, Math.round(point.x)),
        y: Math.max(0, Math.round(point.y)),
      },
      { refreshPreview: true },
    );
  }, [upsertOverlayField]);

  const nudgeOverlayField = useCallback((fieldId: string, deltaX: number, deltaY: number) => {
    const target = overlayFields.find((field) => field.id === fieldId);
    if (!target) return;
    upsertOverlayField(fieldId, {
      x: Math.max(0, target.x + deltaX),
      y: Math.max(0, target.y + deltaY),
    });
  }, [overlayFields, upsertOverlayField]);

  const runValidation = useCallback(async (
    options?: { silent?: boolean; bytes?: Uint8Array }
  ): Promise<PdfValidationModel | null> => {
    if (!session?.session_id || !pdfDocRef.current) return null;
    const silent = options?.silent === true;

    if (!silent) {
      setError(null);
      setInfoMessage(null);
    }
    setValidating(true);
    try {
      const bytes = options?.bytes ?? await serializeWorkingPdfForPersistence();
      const payload = {
        pdf_base64: uint8ToBase64(bytes),
        field_values: buildFieldValuesPayload(false),
      };
      const response = await apiClient.post(`/dms/documents/${docId}/pdf/sessions/${session.session_id}/validate`, payload);
      const validationResult: PdfValidationModel | undefined = response.data?.validation;
      if (validationResult) {
        setValidation(validationResult);
        if (!silent) {
          if (validationResult.readiness.status === 'blockers') {
            setError(`Validation found ${validationResult.readiness.blockers.length} blocker(s). Resolve blockers before commit.`);
          } else if (validationResult.readiness.status === 'warnings') {
            setInfoMessage(`Validation complete with ${validationResult.readiness.warnings.length} warning(s).`);
          } else {
            setInfoMessage('Validation complete. Filing readiness: Ready.');
          }
        }
      }
      setPdfData(bytes.slice(0));
      return validationResult || null;
    } catch (validationError) {
      const message = getApiErrorMessage(validationError, 'Failed to validate PDF filing readiness.');
      setError(message);
      return null;
    } finally {
      setValidating(false);
    }
  }, [buildFieldValuesPayload, docId, serializeWorkingPdfForPersistence, session?.session_id]);

  const saveDraft = useCallback(async (autosave = false) => {
    if (!session?.session_id || !pdfDocRef.current) return;
    setSaving(!autosave);
    setInfoMessage(null);
    setError(null);
    try {
      const bytes = await serializeDraftPdfForPersistence();
      const payload = {
        pdf_base64: uint8ToBase64(bytes),
        field_values: buildFieldValuesPayload(true),
        autosave,
      };
      const response = await apiClient.put(`/dms/documents/${docId}/pdf/sessions/${session.session_id}`, payload);
      const updatedSession: PdfSessionModel = response.data?.session;
      if (updatedSession) {
        setSession(updatedSession);
      }
      void updatePreview(30);
      setDirty(false);
      if (!autosave) {
        setInfoMessage('Draft saved.');
      }
    } catch (saveError) {
      const message = getApiErrorMessage(saveError, 'Failed to save draft.');
      setError(message);
    } finally {
      setSaving(false);
    }
  }, [buildFieldValuesPayload, docId, serializeDraftPdfForPersistence, session?.session_id, updatePreview]);

  useEffect(() => {
    if (!session?.session_id || !dirty || !sessionIsActive) return;
    const intervalId = window.setInterval(() => {
      void saveDraft(true);
    }, 45000);
    return () => window.clearInterval(intervalId);
  }, [dirty, saveDraft, session?.session_id, sessionIsActive]);

  const commitVersion = useCallback(async () => {
    if (!session?.session_id || !pdfDocRef.current) return;
    setCommitting(true);
    setError(null);
    setInfoMessage(null);
    try {
      const bytes = await serializeWorkingPdfForPersistence();
      const validationResult = await runValidation({ silent: true, bytes });
      if (validationResult?.readiness.status === 'blockers') {
        setValidation(validationResult);
        setError(`Cannot commit. ${validationResult.readiness.blockers.length} filing blocker(s) must be resolved first.`);
        return;
      }

      const payload = {
        pdf_base64: uint8ToBase64(bytes),
        field_values: buildFieldValuesPayload(true),
        changes_description: 'PDF edited in EdgeDocs native PDF editor',
      };
      const response = await apiClient.post(`/dms/documents/${docId}/pdf/sessions/${session.session_id}/commit`, payload);
      const committedSession: PdfSessionModel | undefined = response.data?.session;
      const committedVersion = response.data?.version;
      const commitValidation: FilingReadinessModel | undefined = response.data?.validation;
      const commitChangedFields: PdfFieldChangeModel[] = response.data?.changed_fields || [];
      const commitDeadlineSuggestions: DeadlineSuggestionModel[] = response.data?.deadline_suggestions || [];
      if (committedSession) {
        setSession(committedSession);
      }
      if (commitValidation) {
        setValidation({
          readiness: commitValidation,
          changed_fields: commitChangedFields,
          deadline_suggestions: commitDeadlineSuggestions,
          summary: {
            changed_field_count: commitChangedFields.length,
            blocker_count: commitValidation.blockers?.length || 0,
            warning_count: commitValidation.warnings?.length || 0,
          },
        });
      }
      if (committedVersion?.version_id) {
        try {
          await refreshSignatureEvidenceSummary(committedVersion.version_id);
        } catch {
          // Keep commit successful even when evidence summary refresh fails.
        }
      }
      setDirty(false);
      setInfoMessage(`Committed as ${committedVersion?.version_number || 'new version'}. ${commitDeadlineSuggestions.length} deadline suggestion(s) generated.`);
    } catch (commitError) {
      const message = getApiErrorMessage(commitError, 'Failed to commit PDF version.');
      setError(message);
    } finally {
      setCommitting(false);
    }
  }, [buildFieldValuesPayload, docId, refreshSignatureEvidenceSummary, runValidation, serializeWorkingPdfForPersistence, session?.session_id]);

  const abandonSession = useCallback(async () => {
    if (!session?.session_id) {
      navigate('/documents');
      return;
    }
    try {
      await apiClient.delete(`/dms/documents/${docId}/pdf/sessions/${session.session_id}`);
    } finally {
      navigate('/documents');
    }
  }, [docId, navigate, session?.session_id]);

  const handlePrint = useCallback(async () => {
    if (!pdfData) return;
    const pageCount = pdfDocRef.current?.getPageCount() || previewPageCount || 1;
    const approval = await requestPrintBillingApproval({
      sourceType: 'edgedocs',
      sourceId: docId,
      title: documentTitle || 'PDF Document',
      matterId: documentMatterIdRef.current,
      matterDisplayId: matterContext?.matter_number || null,
      clientDisplayId: matterContext?.client?.client_name || null,
      pageCount,
    });
    if (!approval.approved) return;

    const blob = new Blob([pdfData], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;width:0;height:0;border:0;visibility:hidden;';
    iframe.src = url;
    document.body.appendChild(iframe);
    iframe.onload = () => {
      try {
        iframe.contentWindow?.print();
      } catch {
        // Fallback: open in new tab if iframe print is blocked
        window.open(url, '_blank');
      }
      setTimeout(() => {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(url);
      }, 60000);
    };
  }, [docId, documentTitle, matterContext?.client?.client_name, matterContext?.matter_number, pdfData, previewPageCount]);

  const filteredFields = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return fields;
    return fields.filter((field) => field.label.toLowerCase().includes(term) || field.name.toLowerCase().includes(term));
  }, [fields, search]);

  const filteredOverlayFields = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return overlayFields;
    return overlayFields.filter((field) =>
      field.label.toLowerCase().includes(term) || field.name.toLowerCase().includes(term),
    );
  }, [overlayFields, search]);

  const signatureOverlayFields = useMemo(
    () => overlayFields.filter((field) => field.type === 'signature' || field.type === 'initial'),
    [overlayFields],
  );

  // --- Enhancement A: Completion stats ---
  const fieldCompletionStats = useMemo(() => {
    const targetFields = overlayMode ? overlayFields : fields;
    const total = targetFields.length;
    let filled = 0;
    let requiredTotal = 0;
    let requiredFilled = 0;
    let firstEmptyRequiredName: string | null = null;

    for (const field of targetFields) {
      const val = fieldValues[field.name];
      const isEmpty = val === null || val === undefined || val === '' || (Array.isArray(val) && val.length === 0);
      if (!isEmpty) filled++;
      if (field.required) {
        requiredTotal++;
        if (!isEmpty) {
          requiredFilled++;
        } else if (!firstEmptyRequiredName) {
          firstEmptyRequiredName = field.name;
        }
      }
    }
    const requiredRemaining = requiredTotal - requiredFilled;
    const pct = total > 0 ? Math.round((filled / total) * 100) : 0;
    return { total, filled, requiredTotal, requiredRemaining, firstEmptyRequiredName, pct };
  }, [fields, fieldValues, overlayFields, overlayMode]);

  // --- Enhancement B: Page-grouped fields for AcroForm mode ---
  type PageGroup = { page: number | null; label: string; fields: PdfFieldModel[]; requiredRemaining: number };

  const pageGroupedFields = useMemo<PageGroup[]>(() => {
    if (overlayMode) return [];
    const groups = new Map<number | null, PdfFieldModel[]>();
    for (const field of filteredFields) {
      const key = field.page ?? null;
      const group = groups.get(key);
      if (group) { group.push(field); }
      else { groups.set(key, [field]); }
    }
    // Sort page groups numerically, "Other" (null) last
    const sortedKeys = Array.from(groups.keys()).sort((a, b) => {
      if (a === null) return 1;
      if (b === null) return -1;
      return a - b;
    });
    return sortedKeys.map((key) => {
      const groupFields = groups.get(key)!;
      let requiredRemaining = 0;
      for (const f of groupFields) {
        if (!f.required) continue;
        const val = fieldValues[f.name];
        const isEmpty = val === null || val === undefined || val === '' || (Array.isArray(val) && val.length === 0);
        if (isEmpty) requiredRemaining++;
      }
      return {
        page: key,
        label: key !== null ? `Page ${key}` : 'Other',
        fields: groupFields,
        requiredRemaining,
      };
    });
  }, [fieldValues, filteredFields, overlayMode]);

  const [collapsedPages, setCollapsedPages] = useState<Set<number | null>>(new Set());

  const togglePageGroup = useCallback((page: number | null) => {
    setCollapsedPages((prev) => {
      const next = new Set(prev);
      if (next.has(page)) { next.delete(page); }
      else { next.add(page); }
      return next;
    });
  }, []);

  // Auto-expand the page group when PDF viewer navigates to a page
  useEffect(() => {
    if (overlayMode || previewPage < 1) return;
    setCollapsedPages((prev) => {
      if (!prev.has(previewPage)) return prev;
      const next = new Set(prev);
      next.delete(previewPage);
      return next;
    });
  }, [overlayMode, previewPage]);

  const jumpToNextEmptyRequired = useCallback(() => {
    const targetName = fieldCompletionStats.firstEmptyRequiredName;
    if (!targetName) return;
    // For AcroForm mode, find the model and navigate
    const model = fields.find((f) => f.name === targetName);
    if (model) {
      if (model.page) {
        // Expand the page group
        setCollapsedPages((prev) => {
          if (!prev.has(model.page!)) return prev;
          const next = new Set(prev);
          next.delete(model.page!);
          return next;
        });
      }
      handleAcroFieldFocus(targetName);
    }
    // Focus the input control
    const control = fieldControlRefMap.current.get(targetName);
    if (control) {
      window.setTimeout(() => {
        control.scrollIntoView({ behavior: 'smooth', block: 'center' });
        control.focus();
      }, 60);
    }
  }, [fieldCompletionStats.firstEmptyRequiredName, fields, handleAcroFieldFocus]);

  // Enhancement C: Fetch matter context and apply auto-fill
  const runAutoFill = useCallback(async () => {
    const matterId = documentMatterIdRef.current;
    if (!matterId) {
      setInfoMessage('This document is not associated with a matter. Auto-fill requires matter context.');
      return;
    }

    setAutoFillLoading(true);
    setAutoFillResult(null);
    setError(null);
    setInfoMessage(null);

    try {
      // Fetch matter context (with client relation included)
      let context = matterContext;
      if (!context) {
        const response = await apiClient.get(`/matters/${matterId}`);
        const matterData = response.data?.matter || response.data;
        if (!matterData?.matter_id) {
          setInfoMessage('Could not load matter context for auto-fill.');
          return;
        }
        context = matterData as MatterContext;
        setMatterContext(context);
      }

      // Build the label → value map
      const autoFillMap = buildAutoFillMap(context);
      if (autoFillMap.size === 0) {
        setInfoMessage('Matter context is empty. No data available for auto-fill.');
        return;
      }

      // Match fields and apply values
      const targetFields = overlayMode ? overlayFields : fields;
      let filled = 0;
      let skipped = 0;
      const details: Array<{ label: string; value: string }> = [];

      for (const field of targetFields) {
        const currentValue = fieldValues[field.name];
        const isEmpty = currentValue === null || currentValue === undefined || currentValue === '' ||
          (Array.isArray(currentValue) && currentValue.length === 0);

        // Only fill empty fields — never overwrite user input
        if (!isEmpty) {
          skipped++;
          continue;
        }

        // Only auto-fill text-type fields (not checkboxes, radios, dropdowns)
        if (field.type !== 'text' && field.type !== 'unsupported') continue;

        const resolved = resolveAutoFillValue(field.label, autoFillMap);
        if (!resolved) continue;

        updateField(field.name, resolved, { refreshPreview: false });
        filled++;
        details.push({ label: field.label, value: resolved });
      }

      setAutoFillResult({ filled, skipped, details });
      if (filled > 0) {
        updatePreview(100);
        setDirty(true);
        setInfoMessage(`Auto-filled ${filled} field${filled !== 1 ? 's' : ''} from matter context.${skipped > 0 ? ` ${skipped} field${skipped !== 1 ? 's' : ''} already had values.` : ''}`);
      } else {
        setInfoMessage('No empty fields matched the available matter/client data.');
      }
    } catch (autoFillError) {
      setError(getApiErrorMessage(autoFillError, 'Failed to fetch matter context for auto-fill.'));
    } finally {
      setAutoFillLoading(false);
    }
  }, [fields, fieldValues, matterContext, overlayFields, overlayMode, updateField, updatePreview]);

  // --- Clear Form ---
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  /**
   * Whether clearing is allowed. Blocked when:
   * - Session is not active (already committed or abandoned)
   * - Document is in read-only mode
   * - A signature has been committed on this version (evidence chain would break)
   */
  const clearFormAllowed = sessionIsActive && !readOnlyMode && !signatureEvidenceSummary;

  const executeClearForm = useCallback(() => {
    const targetFields = overlayMode ? overlayFields : fields;
    if (targetFields.length === 0) return;

    // Reset all field values to empty
    const clearedValues: Record<string, PdfFieldValue> = {};
    for (const field of targetFields) {
      const emptyValue: PdfFieldValue = field.type === 'checkbox' ? false : '';
      clearedValues[field.name] = emptyValue;

      // Also reset the underlying pdf-lib AcroForm field object if present
      if (!overlayMode) {
        const pdfField = fieldRefMap.current.get(field.name);
        if (pdfField) {
          try { setFieldValue(pdfField, emptyValue); } catch { /* ignore unsupported */ }
        }
      }
    }

    // Preserve overlay schema key if present
    if (overlayFields.length > 0) {
      clearedValues[OVERLAY_SCHEMA_KEY] = serializeOverlaySchema(overlayFields);
    }

    setFieldValues(clearedValues);
    setDirty(true);
    setValidation(null);
    setAutoFillResult(null);
    setShowClearConfirm(false);
    setInfoMessage(`Cleared ${targetFields.length} field${targetFields.length !== 1 ? 's' : ''}. Save draft to persist, or use Auto-Fill to re-populate.`);
    updatePreview(100);
  }, [fields, overlayFields, overlayMode, updatePreview]);

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-100 text-gray-700">
        Loading PDF editor...
      </div>
    );
  }

  if (error && !pdfData) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-100">
        <div className="max-w-lg rounded border border-red-300 bg-red-50 p-6">
          <h2 className="text-lg font-semibold text-red-700">Unable to open PDF editor</h2>
          <p className="mt-2 text-sm text-red-700">{error}</p>
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={() => void loadEditorState()}
              className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              Retry
            </button>
            <button
              type="button"
              onClick={() => navigate('/documents')}
              className="rounded bg-gray-200 px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-300"
            >
              Back to DMS
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-100 text-gray-900 flex flex-col">
      <div className="border-b border-gray-200 bg-white px-4 py-3 flex items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">{documentTitle}</h1>
          <p className="text-xs text-gray-500">
            Native PDF Editor
            {overlayMode ? ' • Overlay mode' : ''}
            {session?.session_id ? ` • Session ${session.session_id.slice(0, 8)}...` : ''}
            {session?.last_saved_at ? ` • Last saved ${new Date(session.last_saved_at).toLocaleString()}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setLexPanelOpen((open) => !open)}
            title="Open Lex for this document"
            aria-label="Open Lex for this document"
            className={`inline-flex h-9 w-9 items-center justify-center rounded-full border transition-colors ${
              lexPanelOpen
                ? 'border-blue-300 bg-blue-50 text-blue-700'
                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            <img src={edgelexLogoStatic} alt="" className="h-6 w-6 rounded-full" />
          </button>
          <button
            type="button"
            onClick={abandonSession}
            className="rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            Exit
          </button>
          <button
            type="button"
            onClick={handlePrint}
            disabled={!pdfData}
            title="Print current document"
            className="rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 disabled:opacity-40 hover:bg-gray-50"
          >
            Print
          </button>
          <button
            type="button"
            onClick={() => setShowClearConfirm(true)}
            disabled={!clearFormAllowed || saving || committing}
            title={signatureEvidenceSummary ? 'Cannot clear a signed document — signature evidence chain would break' : !sessionIsActive ? 'Session is not active' : 'Clear all field values'}
            className="rounded border border-red-300 bg-white px-3 py-2 text-sm font-medium text-red-700 disabled:opacity-40 hover:bg-red-50 transition-colors duration-150"
          >
            Clear Form
          </button>
          <button
            type="button"
            onClick={() => void saveDraft(false)}
            disabled={saving || committing || !dirty || !sessionIsActive}
            className="rounded bg-blue-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-40 hover:bg-blue-700"
          >
            {saving ? 'Saving...' : 'Save Draft'}
          </button>
          <button
            type="button"
            onClick={() => void runValidation()}
            disabled={saving || committing || validating || !sessionIsActive}
            className="rounded bg-amber-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-40 hover:bg-amber-700"
          >
            {validating ? 'Validating...' : 'Validate Filing'}
          </button>
          <button
            type="button"
            onClick={() => void commitVersion()}
            disabled={saving || committing || validating || !sessionIsActive}
            className="rounded bg-emerald-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-40 hover:bg-emerald-700"
          >
            {committing ? 'Committing...' : 'Commit Version'}
          </button>
        </div>
      </div>

      {(error || infoMessage) && (
        <div className={`px-4 py-2 text-sm ${error ? 'bg-red-50 text-red-700 border-b border-red-200' : 'bg-emerald-50 text-emerald-700 border-b border-emerald-200'}`}>
          {error || infoMessage}
        </div>
      )}

      {!sessionIsActive && session && (
        <div className="px-4 py-2 text-sm bg-amber-50 text-amber-700 border-b border-amber-200">
          This session is {session.status}. Start a new edit session to continue editing.
        </div>
      )}

      {readOnlyMode && (
        <div className="px-4 py-2 text-sm bg-slate-100 text-slate-700 border-b border-slate-200">
          Read-only mode: this PDF does not include editable AcroForm fields.
        </div>
      )}

      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_380px] xl:grid-cols-[minmax(0,1fr)_420px] gap-3 p-3">
        <div className="min-h-0 min-w-0 rounded border border-gray-300 bg-white p-3">
          {pdfData ? (
            <PdfViewer
              data={pdfData}
              className="h-full"
              targetPage={targetPreviewPage}
              onPageChange={(page, total) => {
                setPreviewPage(page);
                setPreviewPageCount(total);
              }}
              overlays={overlayPreviewItems}
              activeOverlayId={acroFieldHighlightOverlay?.id || activeOverlayFieldId}
              onOverlaySelect={handleOverlayPreviewSelect}
              onCanvasClick={handleViewerCanvasClick}
              onOverlayMove={handleOverlayPreviewMove}
              onOverlayMoveEnd={handleOverlayPreviewMoveEnd}
            />
          ) : <div className="text-sm text-gray-500">No PDF loaded.</div>}
        </div>

        <div className="min-h-0 min-w-0 rounded border border-gray-300 bg-white p-3 flex flex-col">
          {signatureEvidenceSummary && (
            <div className="mb-3 rounded border border-emerald-200 bg-emerald-50 p-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h2 className="text-sm font-semibold text-emerald-800">Signature Evidence</h2>
                  <p className="mt-1 text-xs text-emerald-700">
                    Policy profile: <span className="font-medium">{signatureEvidenceSummary.policyLabel}</span>
                  </p>
                </div>
                {signatureEvidenceSummary.exportEndpoint && (
                  <button
                    type="button"
                    onClick={() => void downloadSignatureEvidence()}
                    disabled={signatureEvidenceLoading || signatureEvidenceDownloading || !signatureEvidenceSummary.canDownload}
                    className="rounded border border-emerald-300 bg-white px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
                    title={signatureEvidenceSummary.accessDenied ? 'Evidence access is restricted to session owner/admin.' : 'Download evidence package'}
                  >
                    {signatureEvidenceDownloading ? 'Downloading...' : 'Download Evidence Package'}
                  </button>
                )}
              </div>

              <div className="mt-2 space-y-1 text-xs text-emerald-800">
                <p>
                  <span className="font-medium">Signer(s):</span>{' '}
                  {signatureEvidenceSummary.signerNames.length > 0
                    ? signatureEvidenceSummary.signerNames.join(', ')
                    : 'Signer details are available in the evidence package.'}
                </p>
                <p>
                  <span className="font-medium">Signed/Committed:</span>{' '}
                  {formatDateTime(signatureEvidenceSummary.latestSignedAt || signatureEvidenceSummary.committedAt)}
                </p>
                <p>
                  <span className="font-medium">Evidence Hash:</span>{' '}
                  {formatEvidenceHash(signatureEvidenceSummary.evidenceHash)}
                </p>
                <p>
                  <span className="font-medium">Chain Status:</span>{' '}
                  {signatureEvidenceSummary.eventChainValid === true
                    ? 'Valid'
                    : signatureEvidenceSummary.eventChainValid === false
                      ? 'Invalid'
                      : 'Not available'}
                </p>
              </div>

              {signatureEvidenceSummary.generatedAt && (
                <p className="mt-2 text-[11px] text-emerald-700">
                  Evidence generated: {formatDateTime(signatureEvidenceSummary.generatedAt)}
                </p>
              )}
              {signatureEvidenceLoading && (
                <p className="mt-2 text-[11px] text-emerald-700">
                  Checking evidence access permissions...
                </p>
              )}
              {signatureEvidenceSummary.accessDenied && (
                <p className="mt-2 text-[11px] text-amber-700">
                  Evidence download is restricted to the signature session owner or a firm admin.
                </p>
              )}
            </div>
          )}

          {validation && (
            <div className="mb-3 rounded border border-gray-200 bg-gray-50 p-3">
              <div className="flex items-center justify-between gap-2">
                <h2 className="text-sm font-semibold text-gray-800">Filing Readiness</h2>
                <span
                  className={`rounded px-2 py-0.5 text-xs font-medium ${
                    validation.readiness.status === 'ready'
                      ? 'bg-emerald-100 text-emerald-700'
                      : validation.readiness.status === 'warnings'
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-red-100 text-red-700'
                  }`}
                >
                  {validation.readiness.status === 'ready'
                    ? 'Ready'
                    : validation.readiness.status === 'warnings'
                      ? 'Warnings'
                      : 'Blockers'}
                </span>
              </div>
              <p className="mt-1 text-xs text-gray-600">
                {validation.summary.changed_field_count} changed field(s) • {validation.summary.blocker_count} blocker(s) • {validation.summary.warning_count} warning(s)
              </p>
              <p className="mt-1 text-[11px] text-gray-500">
                Click any blocker/warning to jump to that field.
              </p>

              {validation.readiness.blockers.length > 0 && (
                <div className="mt-2 space-y-1">
                  {validation.readiness.blockers.slice(0, 4).map((issue, index) => (
                    <button
                      key={`${issue.code}-${index}`}
                      type="button"
                      onClick={() => jumpToValidationIssue(issue)}
                      className="w-full text-left rounded border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-300"
                    >
                      {(issue.field_label || issue.field_name) ? (
                        <>
                          <span className="font-medium">{issue.field_label || issue.field_name}:</span> {issue.message}
                        </>
                      ) : issue.message}
                    </button>
                  ))}
                </div>
              )}

              {validation.readiness.warnings.length > 0 && (
                <div className="mt-2 space-y-1">
                  {validation.readiness.warnings.slice(0, 4).map((issue, index) => (
                    <button
                      key={`${issue.code}-warn-${index}`}
                      type="button"
                      onClick={() => jumpToValidationIssue(issue)}
                      className="w-full text-left rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-700 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-300"
                    >
                      {(issue.field_label || issue.field_name) ? (
                        <>
                          <span className="font-medium">{issue.field_label || issue.field_name}:</span> {issue.message}
                        </>
                      ) : issue.message}
                    </button>
                  ))}
                </div>
              )}

              {validation.deadline_suggestions.length > 0 && (
                <div className="mt-2 rounded border border-blue-200 bg-blue-50 p-2">
                  <h3 className="text-xs font-semibold text-blue-700">Deadline Suggestions</h3>
                  <div className="mt-1 space-y-1">
                    {validation.deadline_suggestions.slice(0, 4).map((suggestion) => (
                      <div key={suggestion.suggestion_id} className="text-xs text-blue-700">
                        <span className="font-medium">{new Date(suggestion.due_at).toLocaleDateString()}:</span> {suggestion.task_title}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {validation.changed_fields.length > 0 && (
                <div className="mt-2">
                  <h3 className="text-xs font-semibold text-gray-700">Recent Field Changes</h3>
                  <div className="mt-1 space-y-1">
                    {validation.changed_fields.slice(0, 5).map((change) => (
                      <div key={`${change.field_name}-${change.change_type}`} className="rounded border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700">
                        <span className="font-medium">{change.field_label || change.field_name}</span>
                        <span className="mx-1 text-gray-400">•</span>
                        <span className="text-gray-500">{formatFieldValue(change.before)}</span>
                        <span className="mx-1">→</span>
                        <span>{formatFieldValue(change.after)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Enhancement C: Auto-Fill from Matter Context */}
          {!readOnlyMode && sessionIsActive && documentMatterIdRef.current && (
            <div className="mb-3 rounded border border-indigo-200 bg-indigo-50/50 px-3 py-2">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <span className="text-xs font-semibold text-indigo-700">Matter Auto-Fill</span>
                  {matterContext?.matter_number && (
                    <span className="ml-2 text-[11px] text-indigo-500">#{matterContext.matter_number}</span>
                  )}
                </div>
                <button
                  type="button"
                  disabled={autoFillLoading}
                  onClick={runAutoFill}
                  className="rounded bg-indigo-600 px-3 py-1 text-xs font-medium text-white disabled:opacity-50 hover:bg-indigo-700 transition-colors duration-150"
                >
                  {autoFillLoading ? 'Filling...' : autoFillResult ? 'Re-run Auto-Fill' : 'Auto-Fill from Matter'}
                </button>
              </div>
              {autoFillResult && (
                <div className="mt-1.5">
                  <p className="text-[11px] text-indigo-600">
                    Filled {autoFillResult.filled} field{autoFillResult.filled !== 1 ? 's' : ''}
                    {autoFillResult.skipped > 0 && ` • ${autoFillResult.skipped} already had values`}
                  </p>
                  {autoFillResult.details.length > 0 && (
                    <div className="mt-1 max-h-24 overflow-auto space-y-0.5">
                      {autoFillResult.details.map((d, i) => (
                        <div key={i} className="flex items-baseline gap-1 text-[10px]">
                          <span className="font-medium text-indigo-700">{d.label}:</span>
                          <span className="text-indigo-500 truncate">{d.value}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="mb-3">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-sm font-semibold">
                {overlayMode ? `Overlay Fields (${overlayFields.length})` : `Form Fields (${fields.length})`}
              </h2>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => addSignatureOrInitialField('signature')}
                  disabled={!sessionIsActive || readOnlyMode}
                  className="rounded bg-emerald-600 px-2 py-1 text-xs font-medium text-white disabled:opacity-40 hover:bg-emerald-700"
                >
                  Add Signature
                </button>
                <button
                  type="button"
                  onClick={() => addSignatureOrInitialField('initial')}
                  disabled={!sessionIsActive || readOnlyMode}
                  className="rounded bg-teal-600 px-2 py-1 text-xs font-medium text-white disabled:opacity-40 hover:bg-teal-700"
                >
                  Add Initial
                </button>
                <button
                  type="button"
                  onClick={addFreeformTextField}
                  disabled={!sessionIsActive || readOnlyMode}
                  className="rounded bg-violet-600 px-2 py-1 text-xs font-medium text-white disabled:opacity-40 hover:bg-violet-700"
                >
                  Add Text
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (!activeOverlayFieldId) return;
                    removeOverlayField(activeOverlayFieldId);
                  }}
                  disabled={!sessionIsActive || readOnlyMode || !activeOverlayFieldId}
                  className="rounded border border-red-300 bg-white px-2 py-1 text-xs font-medium text-red-700 disabled:opacity-40 hover:bg-red-50"
                >
                  Remove Selected
                </button>
                {overlayMode && (
                  <button
                    type="button"
                    onClick={addOverlayField}
                    disabled={!sessionIsActive || readOnlyMode}
                    className="rounded bg-blue-600 px-2 py-1 text-xs font-medium text-white disabled:opacity-40 hover:bg-blue-700"
                  >
                    Add Field
                  </button>
                )}
              </div>
            </div>
            <div className="mt-2 space-y-2 rounded border border-gray-200 bg-gray-50 p-2">
              <div className="flex items-center justify-between">
                <h3 className="text-[11px] font-semibold text-gray-700">Saved Signatures</h3>
                <button
                  type="button"
                  onClick={() => openPresetModal('signature')}
                  className="rounded border border-gray-300 bg-white px-2 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-100"
                >
                  Create
                </button>
              </div>
              <div className="flex flex-wrap gap-1">
                {signaturePresetOptions.length === 0 ? (
                  <span className="text-[11px] text-gray-500">No signature presets yet.</span>
                ) : signaturePresetOptions.map((preset) => (
                  <div key={preset.id} className="inline-flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setActiveSignaturePresetId(preset.id)}
                      className={`rounded border px-2 py-1 text-[11px] ${
                        activeSignaturePresetId === preset.id
                          ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100'
                      }`}
                      title={preset.label}
                    >
                      {preset.label}
                    </button>
                    <button
                      type="button"
                      onClick={() => createOverlayFieldFromPreset(preset)}
                      disabled={!sessionIsActive || readOnlyMode}
                      className="rounded border border-emerald-300 bg-white px-2 py-1 text-[11px] font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-40"
                    >
                      Place
                    </button>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between">
                <h3 className="text-[11px] font-semibold text-gray-700">Saved Initials</h3>
                <button
                  type="button"
                  onClick={() => openPresetModal('initial')}
                  className="rounded border border-gray-300 bg-white px-2 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-100"
                >
                  Create
                </button>
              </div>
              <div className="flex flex-wrap gap-1">
                {initialPresetOptions.length === 0 ? (
                  <span className="text-[11px] text-gray-500">No initial presets yet.</span>
                ) : initialPresetOptions.map((preset) => (
                  <div key={preset.id} className="inline-flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setActiveInitialPresetId(preset.id)}
                      className={`rounded border px-2 py-1 text-[11px] ${
                        activeInitialPresetId === preset.id
                          ? 'border-teal-500 bg-teal-50 text-teal-700'
                          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100'
                      }`}
                      title={preset.label}
                    >
                      {preset.label}
                    </button>
                    <button
                      type="button"
                      onClick={() => createOverlayFieldFromPreset(preset)}
                      disabled={!sessionIsActive || readOnlyMode}
                      className="rounded border border-teal-300 bg-white px-2 py-1 text-[11px] font-medium text-teal-700 hover:bg-teal-50 disabled:opacity-40"
                    >
                      Place
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <input
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={overlayMode ? 'Search overlay fields...' : 'Search fields...'}
              className="mt-2 w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {(overlayMode || placingOverlayFieldId || signatureOverlayFields.length > 0) && (
              <p className="mt-1 text-[11px] text-gray-500">
                {placingOverlayFieldId
                  ? 'Click inside the PDF preview to place the selected field.'
                  : `Preview page ${previewPage} of ${previewPageCount}. Drag blue field boxes in the PDF to reposition quickly.`}
              </p>
            )}

            {/* Enhancement A: Completion progress strip */}
            {fieldCompletionStats.total > 0 && (
              <div className="mt-2 rounded border border-gray-200 bg-gray-50 px-3 py-2">
                <div className="flex items-center justify-between text-[11px] text-gray-600">
                  <span>{fieldCompletionStats.filled}/{fieldCompletionStats.total} filled</span>
                  <span>{fieldCompletionStats.pct}%</span>
                </div>
                <div className="mt-1 h-1.5 w-full rounded-full bg-gray-200">
                  <div
                    className={`h-1.5 rounded-full transition-all duration-200 ${
                      fieldCompletionStats.pct === 100 ? 'bg-emerald-500' : 'bg-blue-500'
                    }`}
                    style={{ width: `${fieldCompletionStats.pct}%` }}
                  />
                </div>
                <div className="mt-1.5 flex items-center justify-between">
                  {fieldCompletionStats.requiredRemaining > 0 ? (
                    <span className="text-[11px] font-medium text-amber-600">
                      {fieldCompletionStats.requiredRemaining} required field{fieldCompletionStats.requiredRemaining !== 1 ? 's' : ''} remaining
                    </span>
                  ) : fieldCompletionStats.requiredTotal > 0 ? (
                    <span className="text-[11px] font-medium text-emerald-600">All required fields complete</span>
                  ) : (
                    <span className="text-[11px] text-gray-400">No required fields detected</span>
                  )}
                  {fieldCompletionStats.requiredRemaining > 0 && (
                    <button
                      type="button"
                      onClick={jumpToNextEmptyRequired}
                      className="text-[11px] font-medium text-blue-600 hover:text-blue-700 hover:underline"
                    >
                      Jump to next &darr;
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="flex-1 overflow-auto space-y-3 pr-1">
            {overlayMode ? (
              <>
                {filteredOverlayFields.length === 0 && (
                  <div className="text-sm text-gray-500">
                    {overlayFields.length === 0
                      ? 'No overlay fields configured yet. Click Add Field to start editing this non-AcroForm PDF.'
                      : 'No matching overlay fields.'}
                  </div>
                )}

                {filteredOverlayFields.map((field) => {
                  const value = fieldValues[field.name];
                  const isActive = activeOverlayFieldId === field.id;
                  return (
                    <div
                      key={field.id}
                      className={`rounded border px-3 py-2 ${isActive ? 'border-blue-500 bg-blue-50/30' : 'border-gray-200 bg-white'}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <input
                          type="text"
                          value={field.label}
                          disabled={!sessionIsActive || readOnlyMode}
                          onChange={(event) => upsertOverlayField(field.id, { label: event.target.value })}
                          className="w-full rounded border border-gray-300 px-2 py-1 text-sm font-medium"
                        />
                        <button
                          type="button"
                          disabled={!sessionIsActive || readOnlyMode}
                          onClick={() => removeOverlayField(field.id)}
                          className="rounded border border-red-300 px-2 py-1 text-xs text-red-700 disabled:opacity-40 hover:bg-red-50"
                        >
                          Remove
                        </button>
                      </div>

                      <p className="mt-1 text-[10px] text-gray-500" title={field.name}>
                        {field.name}
                      </p>

                      <div className="mt-2 grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <label className="text-[11px] font-medium text-gray-600">Type</label>
                          <select
                            value={field.type}
                            disabled={!sessionIsActive || readOnlyMode}
                            onChange={(event) => upsertOverlayField(field.id, { type: event.target.value as OverlayFieldType })}
                            className="w-full rounded border border-gray-300 px-2 py-1 text-xs"
                          >
                            <option value="text">Text</option>
                            <option value="date">Date</option>
                            <option value="checkbox">Checkbox</option>
                            <option value="signature">Signature</option>
                            <option value="initial">Initial</option>
                            <option value="freeform_text">Free Text</option>
                          </select>
                        </div>
                        <div className="space-y-1">
                          <label className="text-[11px] font-medium text-gray-600">Required</label>
                          <label className="flex items-center gap-2 rounded border border-gray-300 px-2 py-1 text-xs">
                            <input
                              type="checkbox"
                              checked={field.required}
                              disabled={!sessionIsActive || readOnlyMode}
                              onChange={(event) => upsertOverlayField(field.id, { required: event.target.checked })}
                            />
                            Required
                          </label>
                        </div>
                      </div>

                      <div className="mt-2 grid grid-cols-4 gap-2">
                        <label className="space-y-1">
                          <span className="text-[10px] text-gray-600">Page</span>
                          <input
                            type="number"
                            min={1}
                            max={Math.max(1, previewPageCount)}
                            value={field.page}
                            disabled={!sessionIsActive || readOnlyMode}
                            onChange={(event) => upsertOverlayField(field.id, { page: Number(event.target.value) || 1 })}
                            className="w-full rounded border border-gray-300 px-1.5 py-1 text-xs"
                          />
                        </label>
                        <label className="space-y-1">
                          <span className="text-[10px] text-gray-600">X</span>
                          <input
                            type="number"
                            min={0}
                            value={Math.round(field.x)}
                            disabled={!sessionIsActive || readOnlyMode}
                            onChange={(event) => upsertOverlayField(field.id, { x: Number(event.target.value) || 0 })}
                            className="w-full rounded border border-gray-300 px-1.5 py-1 text-xs"
                          />
                        </label>
                        <label className="space-y-1">
                          <span className="text-[10px] text-gray-600">Y</span>
                          <input
                            type="number"
                            min={0}
                            value={Math.round(field.y)}
                            disabled={!sessionIsActive || readOnlyMode}
                            onChange={(event) => upsertOverlayField(field.id, { y: Number(event.target.value) || 0 })}
                            className="w-full rounded border border-gray-300 px-1.5 py-1 text-xs"
                          />
                        </label>
                        <label className="space-y-1">
                          <span className="text-[10px] text-gray-600">Font</span>
                          <input
                            type="number"
                            min={8}
                            max={field.type === 'freeform_text' ? 72 : 24}
                            value={field.font_size}
                            disabled={!sessionIsActive || readOnlyMode}
                            onChange={(event) => upsertOverlayField(field.id, { font_size: Number(event.target.value) || 11 })}
                            className="w-full rounded border border-gray-300 px-1.5 py-1 text-xs"
                          />
                        </label>
                      </div>

                      <div className="mt-2 grid grid-cols-2 gap-2">
                        <label className="space-y-1">
                          <span className="text-[10px] text-gray-600">Width</span>
                          <input
                            type="number"
                            min={24}
                            value={Math.round(field.width)}
                            disabled={!sessionIsActive || readOnlyMode}
                            onChange={(event) => upsertOverlayField(field.id, { width: Number(event.target.value) || 220 })}
                            className="w-full rounded border border-gray-300 px-1.5 py-1 text-xs"
                          />
                        </label>
                        <label className="space-y-1">
                          <span className="text-[10px] text-gray-600">Height</span>
                          <input
                            type="number"
                            min={16}
                            value={Math.round(field.height)}
                            disabled={!sessionIsActive || readOnlyMode}
                            onChange={(event) => upsertOverlayField(field.id, { height: Number(event.target.value) || 20 })}
                            className="w-full rounded border border-gray-300 px-1.5 py-1 text-xs"
                          />
                        </label>
                      </div>

                      {field.type === 'freeform_text' && (
                        <div className="mt-2 space-y-2 rounded border border-violet-200 bg-violet-50/40 p-2">
                          <div className="space-y-1">
                            <label className="text-[11px] font-medium text-gray-600">Text Content</label>
                            <textarea
                              rows={3}
                              value={typeof fieldValues[field.name] === 'string' ? fieldValues[field.name] as string : ''}
                              disabled={!sessionIsActive || readOnlyMode}
                              placeholder="Type your annotation text here..."
                              onChange={(event) => {
                                const newText = event.target.value.slice(0, 5000);
                                setFieldValues((prev) => ({ ...prev, [field.name]: newText }));
                                setDirty(true);
                                void updatePreview(300);
                              }}
                              className="w-full rounded border border-gray-300 px-2 py-1 text-xs resize-none focus:outline-none focus:ring-1 focus:ring-violet-400"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1">
                              <label className="text-[11px] font-medium text-gray-600">Font</label>
                              <select
                                value={field.annotation_style?.font_family || 'Helvetica'}
                                disabled={!sessionIsActive || readOnlyMode}
                                onChange={(event) => {
                                  upsertOverlayField(field.id, {
                                    annotation_style: {
                                      font_family: event.target.value as TextAnnotationFontFamily,
                                      color: field.annotation_style?.color || '#000000',
                                      bold: field.annotation_style?.bold ?? false,
                                      italic: field.annotation_style?.italic ?? false,
                                    },
                                  });
                                }}
                                className="w-full rounded border border-gray-300 px-1.5 py-1 text-xs"
                              >
                                <option value="Helvetica">Helvetica</option>
                                <option value="Times-Roman">Times New Roman</option>
                                <option value="Courier">Courier</option>
                              </select>
                            </div>
                            <div className="space-y-1">
                              <label className="text-[11px] font-medium text-gray-600">Color</label>
                              <input
                                type="color"
                                value={field.annotation_style?.color || '#000000'}
                                disabled={!sessionIsActive || readOnlyMode}
                                onChange={(event) => {
                                  upsertOverlayField(field.id, {
                                    annotation_style: {
                                      font_family: field.annotation_style?.font_family || 'Helvetica',
                                      color: event.target.value,
                                      bold: field.annotation_style?.bold ?? false,
                                      italic: field.annotation_style?.italic ?? false,
                                    },
                                  });
                                }}
                                className="w-full h-7 rounded border border-gray-300 px-0.5 py-0.5 text-xs cursor-pointer"
                              />
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <label className="flex items-center gap-1.5 text-xs text-gray-700">
                              <input
                                type="checkbox"
                                checked={field.annotation_style?.bold ?? false}
                                disabled={!sessionIsActive || readOnlyMode}
                                onChange={(event) => {
                                  upsertOverlayField(field.id, {
                                    annotation_style: {
                                      font_family: field.annotation_style?.font_family || 'Helvetica',
                                      color: field.annotation_style?.color || '#000000',
                                      bold: event.target.checked,
                                      italic: field.annotation_style?.italic ?? false,
                                    },
                                  });
                                }}
                              />
                              <span className="font-bold">Bold</span>
                            </label>
                            <label className="flex items-center gap-1.5 text-xs text-gray-700">
                              <input
                                type="checkbox"
                                checked={field.annotation_style?.italic ?? false}
                                disabled={!sessionIsActive || readOnlyMode}
                                onChange={(event) => {
                                  upsertOverlayField(field.id, {
                                    annotation_style: {
                                      font_family: field.annotation_style?.font_family || 'Helvetica',
                                      color: field.annotation_style?.color || '#000000',
                                      bold: field.annotation_style?.bold ?? false,
                                      italic: event.target.checked,
                                    },
                                  });
                                }}
                              />
                              <span className="italic">Italic</span>
                            </label>
                          </div>
                        </div>
                      )}

                      <div className="mt-2 grid grid-cols-4 gap-1">
                        <button
                          type="button"
                          disabled={!sessionIsActive || readOnlyMode}
                          onClick={() => nudgeOverlayField(field.id, 0, -1)}
                          className="rounded border border-gray-300 px-1 py-1 text-[11px] text-gray-700 disabled:opacity-40 hover:bg-gray-50"
                          title="Move up 1"
                        >
                          Up
                        </button>
                        <button
                          type="button"
                          disabled={!sessionIsActive || readOnlyMode}
                          onClick={() => nudgeOverlayField(field.id, 0, 1)}
                          className="rounded border border-gray-300 px-1 py-1 text-[11px] text-gray-700 disabled:opacity-40 hover:bg-gray-50"
                          title="Move down 1"
                        >
                          Down
                        </button>
                        <button
                          type="button"
                          disabled={!sessionIsActive || readOnlyMode}
                          onClick={() => nudgeOverlayField(field.id, -1, 0)}
                          className="rounded border border-gray-300 px-1 py-1 text-[11px] text-gray-700 disabled:opacity-40 hover:bg-gray-50"
                          title="Move left 1"
                        >
                          Left
                        </button>
                        <button
                          type="button"
                          disabled={!sessionIsActive || readOnlyMode}
                          onClick={() => nudgeOverlayField(field.id, 1, 0)}
                          className="rounded border border-gray-300 px-1 py-1 text-[11px] text-gray-700 disabled:opacity-40 hover:bg-gray-50"
                          title="Move right 1"
                        >
                          Right
                        </button>
                      </div>

                      <div className="mt-2">
                        {field.type === 'checkbox' ? (
                          <label className="flex items-center justify-between rounded border border-gray-300 px-2 py-1 text-xs">
                            <span>Checked</span>
                            <input
                              ref={(element) => registerFieldControlRef(field.name, element)}
                              type="checkbox"
                              checked={Boolean(value)}
                              disabled={!sessionIsActive || readOnlyMode}
                              onChange={(event) => updateField(field.name, event.target.checked)}
                            />
                          </label>
                        ) : field.type === 'signature' || field.type === 'initial' ? (
                          <div className="space-y-2">
                            <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2">
                              <select
                                value={
                                  field.type === 'signature'
                                    ? (field.signature_preset_id || activeSignaturePresetId || '')
                                    : (field.signature_preset_id || activeInitialPresetId || '')
                                }
                                disabled={!sessionIsActive || readOnlyMode}
                                onChange={(event) => {
                                  const selectedPreset = signaturePresets.find((preset) => preset.id === event.target.value) || null;
                                  if (!selectedPreset) return;
                                  applyPresetToOverlayField(field.id, selectedPreset);
                                }}
                                className="w-full rounded border border-gray-300 px-2 py-1 text-xs"
                              >
                                <option value="">Select preset...</option>
                                {(field.type === 'signature' ? signaturePresetOptions : initialPresetOptions).map((preset) => (
                                  <option key={preset.id} value={preset.id}>
                                    {preset.label}
                                  </option>
                                ))}
                              </select>
                              <button
                                type="button"
                                disabled={!sessionIsActive || readOnlyMode}
                                onClick={() => openPresetModal(field.type === 'signature' ? 'signature' : 'initial')}
                                className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                              >
                                New
                              </button>
                            </div>

                            <input
                              ref={(element) => registerFieldControlRef(field.name, element)}
                              type="text"
                              value={typeof value === 'string' ? value : ''}
                              disabled={!sessionIsActive || readOnlyMode}
                              onFocus={cancelPendingPreview}
                              onChange={(event) => {
                                updateField(field.name, event.target.value, { refreshPreview: false });
                                updatePreview(350);
                              }}
                              onBlur={() => updatePreview(150)}
                              className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                              style={field.type === 'signature' ? { fontFamily: signatureFontFamily(field.signature_style), fontSize: `${Math.max(14, field.font_size)}px` } : undefined}
                              placeholder={field.type === 'signature' ? 'Type signature or select preset' : 'Type initials or select preset'}
                            />
                          </div>
                        ) : (
                          <input
                            ref={(element) => registerFieldControlRef(field.name, element)}
                            type={field.type === 'date' ? 'date' : 'text'}
                            value={typeof value === 'string' ? value : ''}
                            disabled={!sessionIsActive || readOnlyMode}
                            onFocus={cancelPendingPreview}
                            onChange={(event) => {
                              updateField(field.name, event.target.value, { refreshPreview: false });
                              updatePreview(500);
                            }}
                            onBlur={() => updatePreview(200)}
                            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                          />
                        )}
                      </div>

                      <div className="mt-2 flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setActiveOverlayFieldId(field.id);
                            setTargetPreviewPage(field.page);
                          }}
                          className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
                        >
                          Focus
                        </button>
                        <button
                          type="button"
                          disabled={!sessionIsActive || readOnlyMode}
                          onClick={() => beginOverlayPlacement(field.id)}
                          className={`rounded px-2 py-1 text-xs font-medium ${
                            placingOverlayFieldId === field.id
                              ? 'bg-amber-600 text-white'
                              : 'bg-blue-600 text-white hover:bg-blue-700'
                          } disabled:opacity-40`}
                        >
                          {placingOverlayFieldId === field.id ? 'Click to place...' : 'Place on PDF'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </>
            ) : (
              <>
                {filteredFields.length === 0 && (
                  <div className="text-sm text-gray-500">
                    {fields.length === 0 ? 'No editable AcroForm fields were detected in this PDF.' : 'No matching fields.'}
                  </div>
                )}

                {/* Enhancement B: Page-grouped collapsible sections */}
                {pageGroupedFields.map((group) => {
                  const isCollapsed = collapsedPages.has(group.page);
                  return (
                    <div key={group.page ?? 'other'} className="rounded border border-gray-200">
                      <button
                        type="button"
                        onClick={() => togglePageGroup(group.page)}
                        className="flex w-full items-center justify-between gap-2 rounded-t bg-gray-50 px-3 py-1.5 text-left hover:bg-gray-100 transition-colors duration-150"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-[11px] text-gray-400">{isCollapsed ? '▶' : '▼'}</span>
                          <span className="text-xs font-semibold text-gray-700">{group.label}</span>
                          <span className="text-[11px] text-gray-500">({group.fields.length} field{group.fields.length !== 1 ? 's' : ''})</span>
                        </div>
                        {group.requiredRemaining > 0 ? (
                          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                            {group.requiredRemaining} required
                          </span>
                        ) : group.fields.some((f) => f.required) ? (
                          <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">
                            Complete
                          </span>
                        ) : null}
                      </button>
                      {!isCollapsed && (
                        <div className="space-y-2 px-3 py-2">
                          {group.fields.map((field) => {
                  const value = fieldValues[field.name];
                  const isEmpty = value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0);
                  const isRequiredEmpty = field.required && isEmpty;
                  const requiredBadge = field.required
                    ? <span className="ml-1 text-[10px] font-semibold text-red-500" title="Required field">*</span>
                    : null;
                  if (field.type === 'checkbox') {
                    return (
                      <label key={field.name} className={`flex items-center justify-between rounded border px-3 py-2 text-sm ${isRequiredEmpty ? 'border-amber-300 bg-amber-50/40' : 'border-gray-200'}`}>
                        <span title={field.name}>{field.label}{requiredBadge}</span>
                        <input
                          ref={(element) => registerFieldControlRef(field.name, element)}
                          type="checkbox"
                          checked={Boolean(value)}
                          disabled={!sessionIsActive}
                          onFocus={() => handleAcroFieldFocus(field.name)}
                          onChange={(event) => updateField(field.name, event.target.checked)}
                        />
                      </label>
                    );
                  }

                  if (field.type === 'dropdown') {
                    return (
                      <div key={field.name} className="space-y-1">
                        <label className="text-xs font-medium text-gray-600" title={field.name}>{field.label}{requiredBadge}</label>
                        <select
                          ref={(element) => registerFieldControlRef(field.name, element)}
                          value={typeof value === 'string' ? value : ''}
                          disabled={!sessionIsActive}
                          onFocus={() => handleAcroFieldFocus(field.name)}
                          onChange={(event) => updateField(field.name, event.target.value)}
                          className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                        >
                          <option value="">Select...</option>
                          {(field.options || []).map((option) => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      </div>
                    );
                  }

                  if (field.type === 'option_list') {
                    return (
                      <div key={field.name} className="space-y-1">
                        <label className="text-xs font-medium text-gray-600" title={field.name}>{field.label}{requiredBadge}</label>
                        <select
                          ref={(element) => registerFieldControlRef(field.name, element)}
                          multiple
                          value={Array.isArray(value) ? value : []}
                          disabled={!sessionIsActive}
                          onFocus={() => handleAcroFieldFocus(field.name)}
                          onChange={(event) => {
                            const selected = Array.from(event.target.selectedOptions).map((option) => option.value);
                            updateField(field.name, selected);
                          }}
                          className="w-full min-h-20 rounded border border-gray-300 px-2 py-1.5 text-sm"
                        >
                          {(field.options || []).map((option) => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      </div>
                    );
                  }

                  if (field.type === 'radio') {
                    const selectedValue = typeof value === 'string' ? value : '';
                    return (
                      <div key={field.name} className={`space-y-1 rounded border px-3 py-2 ${isRequiredEmpty ? 'border-amber-300 bg-amber-50/40' : 'border-gray-200'}`}>
                        <label className="text-xs font-medium text-gray-600" title={field.name}>{field.label}{requiredBadge}</label>
                        <div className="space-y-1">
                          {(field.options || []).map((option, index) => (
                            <label key={option} className="flex items-center gap-2 text-sm">
                              <input
                                ref={index === 0 ? (element) => registerFieldControlRef(field.name, element) : undefined}
                                type="radio"
                                name={field.name}
                                value={option}
                                checked={selectedValue === option}
                                disabled={!sessionIsActive}
                                onFocus={() => handleAcroFieldFocus(field.name)}
                                onChange={(event) => updateField(field.name, event.target.value)}
                              />
                              <span>{option}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  }

                  if (field.type === 'unsupported') {
                    return (
                      <div key={field.name} className="rounded border border-gray-200 px-3 py-2 text-sm text-gray-500">
                        <span title={field.name}>{field.label}{requiredBadge}</span> (unsupported field type)
                      </div>
                    );
                  }

                  return (
                    <div key={field.name} className={`space-y-1 ${isRequiredEmpty ? 'rounded border border-amber-300 bg-amber-50/40 px-2 py-1.5' : ''}`}>
                      <label className="text-xs font-medium text-gray-600" title={field.name}>{field.label}{requiredBadge}</label>
                      <input
                        ref={(element) => registerFieldControlRef(field.name, element)}
                        type="text"
                        value={typeof value === 'string' ? value : ''}
                        disabled={!sessionIsActive}
                        onFocus={() => { cancelPendingPreview(); handleAcroFieldFocus(field.name); }}
                        onChange={(event) => {
                          updateField(field.name, event.target.value, { refreshPreview: false });
                          updatePreview(900);
                        }}
                        onBlur={() => { updatePreview(350); handleAcroFieldBlur(); }}
                        className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                      />
                    </div>
                  );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}

                {signatureOverlayFields.length > 0 && (
                  <div className="mt-2 rounded border border-gray-200 bg-gray-50 p-2">
                    <h3 className="text-xs font-semibold text-gray-700">
                      Signature Placements ({signatureOverlayFields.length})
                    </h3>
                    <div className="mt-2 space-y-2">
                      {signatureOverlayFields.map((field) => (
                        <div key={field.id} className="rounded border border-gray-200 bg-white p-2">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-xs font-medium text-gray-700">
                              {field.label} ({field.type})
                            </span>
                            <button
                              type="button"
                              disabled={!sessionIsActive || readOnlyMode}
                              onClick={() => removeOverlayField(field.id)}
                              className="rounded border border-red-300 px-2 py-1 text-[11px] text-red-700 disabled:opacity-40 hover:bg-red-50"
                            >
                              Remove
                            </button>
                          </div>
                          <div className="mt-2 grid grid-cols-4 gap-2">
                            <label className="space-y-1">
                              <span className="text-[10px] text-gray-600">Page</span>
                              <input
                                type="number"
                                min={1}
                                max={Math.max(1, previewPageCount)}
                                value={field.page}
                                disabled={!sessionIsActive || readOnlyMode}
                                onChange={(event) => upsertOverlayField(field.id, { page: Number(event.target.value) || 1 })}
                                className="w-full rounded border border-gray-300 px-1.5 py-1 text-xs"
                              />
                            </label>
                            <label className="space-y-1">
                              <span className="text-[10px] text-gray-600">X</span>
                              <input
                                type="number"
                                min={0}
                                value={Math.round(field.x)}
                                disabled={!sessionIsActive || readOnlyMode}
                                onChange={(event) => upsertOverlayField(field.id, { x: Number(event.target.value) || 0 })}
                                className="w-full rounded border border-gray-300 px-1.5 py-1 text-xs"
                              />
                            </label>
                            <label className="space-y-1">
                              <span className="text-[10px] text-gray-600">Y</span>
                              <input
                                type="number"
                                min={0}
                                value={Math.round(field.y)}
                                disabled={!sessionIsActive || readOnlyMode}
                                onChange={(event) => upsertOverlayField(field.id, { y: Number(event.target.value) || 0 })}
                                className="w-full rounded border border-gray-300 px-1.5 py-1 text-xs"
                              />
                            </label>
                            <label className="space-y-1">
                              <span className="text-[10px] text-gray-600">Font</span>
                              <input
                                type="number"
                                min={8}
                                max={24}
                                value={field.font_size}
                                disabled={!sessionIsActive || readOnlyMode}
                                onChange={(event) => upsertOverlayField(field.id, { font_size: Number(event.target.value) || 11 })}
                                className="w-full rounded border border-gray-300 px-1.5 py-1 text-xs"
                              />
                            </label>
                          </div>
                          <div className="mt-2 flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                setActiveOverlayFieldId(field.id);
                                setTargetPreviewPage(field.page);
                              }}
                              className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
                            >
                              Focus
                            </button>
                            <button
                              type="button"
                              disabled={!sessionIsActive || readOnlyMode}
                              onClick={() => beginOverlayPlacement(field.id)}
                              className={`rounded px-2 py-1 text-xs font-medium ${
                                placingOverlayFieldId === field.id
                                  ? 'bg-amber-600 text-white'
                                  : 'bg-blue-600 text-white hover:bg-blue-700'
                              } disabled:opacity-40`}
                            >
                              {placingOverlayFieldId === field.id ? 'Click to place...' : 'Place on PDF'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {showPresetModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl rounded-lg border border-gray-200 bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <div>
                <h2 className="text-base font-semibold text-gray-900">
                  Create {presetKind === 'signature' ? 'Signature' : 'Initial'} Preset
                </h2>
                <p className="text-xs text-gray-500">
                  Save once and place on PDF with one click.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowPresetModal(false)}
                className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>

            <div className="space-y-3 px-4 py-3">
              <div className="grid grid-cols-2 gap-3">
                <label className="space-y-1">
                  <span className="text-xs font-medium text-gray-600">Label</span>
                  <input
                    type="text"
                    value={presetLabel}
                    onChange={(event) => setPresetLabel(event.target.value)}
                    className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                    placeholder={presetKind === 'signature' ? 'My Signature' : 'My Initial'}
                  />
                </label>
                <div className="space-y-1">
                  <span className="text-xs font-medium text-gray-600">Input Mode</span>
                  <div className="flex items-center gap-1">
                    {(['draw', 'type', 'upload'] as SignaturePresetMode[]).map((mode) => (
                      <button
                        key={mode}
                        type="button"
                        onClick={() => {
                          setPresetMode(mode);
                          setPresetPreviewError(null);
                        }}
                        className={`rounded border px-2 py-1 text-xs font-medium ${
                          presetMode === mode
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {mode === 'draw' ? 'Draw' : mode === 'type' ? 'Type' : 'Upload'}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {presetMode === 'draw' && (
                <div className="space-y-2">
                  <canvas
                    ref={presetCanvasRef}
                    className="w-full rounded border border-gray-300 bg-white"
                    onPointerDown={handlePresetCanvasPointerDown}
                    onPointerMove={handlePresetCanvasPointerMove}
                    onPointerUp={finishPresetCanvasStroke}
                    onPointerLeave={finishPresetCanvasStroke}
                  />
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-gray-500">
                      {isDrawingPreset ? 'Drawing...' : 'Use mouse, trackpad, or stylus to draw.'}
                    </span>
                    <button
                      type="button"
                      onClick={clearDrawnPresetCanvas}
                      className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
                    >
                      Clear
                    </button>
                  </div>
                </div>
              )}

              {presetMode === 'type' && (
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-3">
                    <label className="space-y-1">
                      <span className="text-xs font-medium text-gray-600">Typed Value</span>
                      <input
                        type="text"
                        value={typedPresetValue}
                        onChange={(event) => setTypedPresetValue(event.target.value)}
                        className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                        placeholder={presetKind === 'signature' ? 'Dwayne David Duncan' : 'DDD'}
                      />
                    </label>
                    <label className="space-y-1">
                      <span className="text-xs font-medium text-gray-600">Style</span>
                      <select
                        value={typedPresetStyle}
                        onChange={(event) => setTypedPresetStyle(event.target.value as SignatureTypedStyle)}
                        className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                      >
                        {signatureStyleOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <div className="rounded border border-gray-200 bg-gray-50 px-3 py-4">
                    <span className="text-[11px] text-gray-500">Preview</span>
                    <p
                      key={typedPresetStyle}
                      className="mt-1 break-all text-2xl text-gray-900"
                      style={{ fontFamily: signatureFontFamily(typedPresetStyle) }}
                    >
                      {typedPresetValue || (presetKind === 'signature' ? 'Your Signature' : 'Your Initial')}
                    </p>
                  </div>
                </div>
              )}

              {presetMode === 'upload' && (
                <div className="space-y-2">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={onPresetUploadChange}
                    className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                  />
                  {uploadedPresetDataUrl && (
                    <div className="rounded border border-gray-200 bg-gray-50 p-2">
                      <img src={uploadedPresetDataUrl} alt="Signature preset preview" className="max-h-28 object-contain" />
                    </div>
                  )}
                </div>
              )}

              {presetPreviewError && (
                <p className="rounded border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                  {presetPreviewError}
                </p>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-4 py-3">
              <button
                type="button"
                onClick={() => setShowPresetModal(false)}
                className="rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSavePreset}
                className="rounded bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                Save Preset
              </button>
            </div>
          </div>
        </div>
      )}

      {lexPanelOpen && (
        <div className="fixed inset-y-0 right-0 z-40 flex w-full max-w-[440px] flex-col border-l border-gray-200 bg-slate-50 shadow-2xl">
          <div className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3">
            <div className="flex min-w-0 items-center gap-2">
              <img src={edgelexLogoStatic} alt="" className="h-7 w-7 rounded-full" />
              <div className="min-w-0">
                <h2 className="text-sm font-semibold text-slate-900">Lex</h2>
                <p className="truncate text-xs text-slate-500">{documentTitle}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setLexPanelOpen(false)}
              className="rounded border border-slate-200 px-2 py-1 text-xs text-slate-600 hover:bg-slate-100"
            >
              Close
            </button>
          </div>
          <LexConversationStream messages={lexMessages} isLoading={lexLoading} />
          <LexInputBar
            onSend={(text) => void sendLexMessage(text)}
            onOpenAttach={() => setLexAttachOpen(true)}
            onRemoveAttachment={removeLexAttachment}
            onDocumentAction={(action) => void sendLexDocumentAction(action)}
            attachedDocuments={lexAttachedDocuments}
            isLoading={lexLoading}
          />
        </div>
      )}

      <AttachmentPickerModal
        open={lexAttachOpen}
        onClose={() => setLexAttachOpen(false)}
        internalMode
        modeHint="Load another EdgeDocs document or local file into Lex."
        onAddDmsSelection={attachLexDmsDocument}
        onAddLocalFiles={(files) => {
          void attachLexLocalFiles(files);
          setLexAttachOpen(false);
        }}
      />

      {/* Clear Form confirmation dialog */}
      {showClearConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40"
            style={{ animation: 'lex-overlay-in 150ms ease' }}
            onClick={() => setShowClearConfirm(false)}
          />
          <div
            className="relative z-10 w-full max-w-md rounded-lg border border-gray-200 bg-white p-6 shadow-lg"
            style={{ animation: 'lex-modal-in 220ms cubic-bezier(0.2, 0.8, 0.2, 1)' }}
          >
            <h3 className="text-base font-semibold text-gray-900">Clear all form fields?</h3>
            <p className="mt-2 text-sm text-gray-600">
              This will erase all values from every field in this form. This action cannot be undone.
            </p>
            <p className="mt-1 text-sm text-gray-500">
              Your changes will not be saved until you click Save Draft.
            </p>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowClearConfirm(false)}
                className="rounded border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors duration-150"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={executeClearForm}
                className="rounded bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 transition-colors duration-150"
              >
                Clear All Fields
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
